import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { utils, writeFile } from 'xlsx';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { selectAuth } from '@/redux/auth/selectors';
import {
  calculateTotalImplementationCost,
  calculateTotalNetRecurringBenefit,
  calculateTotalOneTimeBenefit,
  calculateTotalImplementationCostLatest,
  calculateTotalNetRecurringBenefitsLatest,
  calculateTotalOneTimeBenefitsLatest,
  calculateTotalActualImpact,
  calculateTotalPlannedImpact,
  calculateTotalImpact,
  calculateTotalActualImpactExcl,
  calculateTotalPlannedImpactExcl,
  calculateTotalLatestEstimateImpact,
  calculateTotalLatestEstimateImpactInc,
  calculateNumberOfMilestones,
  countUniqueOwners,
  calculateMilestonesPerOwnerAverage,
  calculateAverageMilestonesPerInitiative,
  countLateMilestones,
  calculateLateMilestoneProportion,
  calculateProportionOfCompletedMilestones,
  calculateTotalActionItem,
  calculateActionItemOwners,
  calculateActionItemPerOwnerAverage,
  countLateWorkstreamActions,
  proportionLateWorkstreamActions,
  proportionCompletedWorkstreamActions,
  calculateTotalInitiativeOwners,
  perOwnerCount,
  calculateStageNetRecurringBenefitsLatest,
  calculateStagesTotalNetRecurringBenefitsLatest,
  calculateChangeNetRecurringBenefit,
  calculateLWRecurringBenefit,
  calculateKPIPlan,
  calculateKPIActual,
  calculatePlannedFTPImpact,
  calculateActualFTPImpact,
  calculateDepositKPIActual,
  calculateDepositKPIPlan,
  calculateLoanKPIPlan,
  calculateFinancingKPIPlan,
  calculateFCYKPIPlan,
  calculateLoanKPIActual,
  calculateFinancingKPIActual,
  calculateFCYKPIActual,
  calculateAccountKPIPlan,
  calculateAccountKPIActual
} from '@/utils/calculations';
import calculateCurrentIndexes from '../KPI/calculateIndex';
import determineStage from '@/utils/DetermineStage';
import checkWorkstreamLeadApprovalStatus from '@/utils/Sort/checkWorkstreamLeadApprovalStatus';
import checkWorkstreamSponsorApprovalStatus from '@/utils/Sort/checkWorkstreamSponsorApprovalStatus';
import checkToFinanceApprovalStatus from '@/utils/Sort/checkTOFinanceApprovalStatus';
import checkStrategyAnalystApprovalStatus from '@/utils/Sort/checkStrategyAnalystApprovalStatus';
import getNetRecurringBenefitCategory from '@/utils/Sort/getNetRecurringBenefitCatagory';
import getNetRecurringBenefitChangeCategoryL0Plus from '@/utils/color/getNetRecurringBenefitCatagoryL0Plus';
import getNetRecurringBenefitChangeCategoryL1Plus from '@/utils/color/getNetRecurringBenefitCatagoryL1Plus';
import getNetRecurringBenefitChangeCategoryL3Plus from '@/utils/color/getNetRecurringBenefitCatagoryL3Plus';
import getNetRecurringBenefitChangeCategoryL2Plus from '@/utils/color/getNetRecurringBenefitCatagoryL2Plus';
import getNetRecurringBenefitChangeCategoryL4Plus from '@/utils/color/getNetRecurringBenefitCatagoryL4Plus';
import getComplianceStatus from '@/utils/Sort/complianceCheck';
import sortInitiativesByActuals from '@/utils/Sort/sortByActual';
import L0PlanDate from '@/utils/Sort/checkDate/l0ActualDate';
import L1PlanDate from '@/utils/Sort/checkDate/L1PlanDate';
import L2ActualDate from '@/utils/Sort/checkDate/l2ActualDate';
import L2PlanDate from '@/utils/Sort/checkDate/l2PlanDate';
import L3ActualDate from '@/utils/Sort/checkDate/l3ActualDate';
import L3PlanDate from '@/utils/Sort/checkDate/l3PlanDate';
import L4ActualDate from '@/utils/Sort/checkDate/l4ActualDate';
import L4PlanDate from '@/utils/Sort/checkDate/l4PlanDate';
import L5ActualDate from '@/utils/Sort/checkDate/l5ActualDate';
import L5PlanDate from '@/utils/Sort/checkDate/l5PlanDate';
import SubmittedForL1PlanDate from '@/utils/Sort/checkDate/submittedForL1ApprovalPlanDate';
import SubmittedForL2PlanDate from '@/utils/Sort/checkDate/submittedForL2ApprovalPlanDate';
import SubmittedForL3PlanDate from '@/utils/Sort/checkDate/submittedForL3ApprovalPlanDate';
import SubmittedForL4PlanDate from '@/utils/Sort/checkDate/submittedForL4ApprovalPlanDate';
import SubmittedForL5PlanDate from '@/utils/Sort/checkDate/submittedForL5ApprovalPlanDate';
import LLatestActualDate from '@/utils/Sort/checkDate/LLatestActualDate';
import getFrequencyAndDirection from '@/utils/color/getFrequencyAndDirection';
import InitiativePlanningSummary from '@/utils/initiativePlanningSummary';
import getSubworkstreamCatagory from '@/utils/color/getSubworkstreamCatagory';
import { useWorkstreamData } from './DataHooks';
import getTypeOfImpact from '@/utils/color/getTypeOfImpact';
import getImplementationCostCatagory from '@/utils/Sort/getImplementationCostCatagory';
import sortInitiativesByActualImpact from '@/utils/Sort/sortByActualImpact';
import moment from 'moment';
import getSector from '@/utils/color/getSector';
import getSubsector from '@/utils/color/getSubsector';
import {
  getSegmentalPBT,
  L1OriginalPlanDate,
  L2OriginalPlanDate,
  L3OriginalPlanDate,
  L4OriginalPlanDate,
  L5OriginalPlanDate,
  // Fiscal period functions for initiatives
  getStageActualYear,
  getStageActualQuarter,
  getStageActualMonth,
  getStageActualWeek,
  getStagePlanYear,
  getStagePlanQuarter,
  getStagePlanMonth,
  getStagePlanWeek,
  getStageOriginalPlanYear,
  getStageOriginalPlanQuarter,
  getStageOriginalPlanMonth,
  getStageOriginalPlanWeek,
  getCancellationYear,
  getCancellationQuarter,
  getCancellationMonth,
  getCancellationWeek,
  // Fiscal period functions for action items
  getActionItemCreatedOnYear,
  getActionItemCreatedOnQuarter,
  getActionItemCreatedOnMonth,
  getActionItemCreatedOnWeek,
  getActionItemUpdatedOnYear,
  getActionItemUpdatedOnQuarter,
  getActionItemUpdatedOnMonth,
  getActionItemUpdatedOnWeek,
  // Fiscal period functions for milestones
  getMilestoneCreatedOnYear,
  getMilestoneCreatedOnQuarter,
  getMilestoneCreatedOnMonth,
  getMilestoneCreatedOnWeek,
  getMilestoneUpdatedOnYear,
  getMilestoneUpdatedOnQuarter,
  getMilestoneUpdatedOnMonth,
  getMilestoneUpdatedOnWeek,
  getMilestoneStartYear,
  getMilestoneStartQuarter,
  getMilestoneStartMonth,
  getMilestoneStartWeek,
  getMilestoneEndYear,
  getMilestoneEndQuarter,
  getMilestoneEndMonth,
  getMilestoneEndWeek
} from '@/utils/Sort/checkDate/getDate';
// Helper function to safely convert any value to string for rendering
const safeStringify = (value) => {
  if (value === null || value === undefined) {
    return 'Not Set';
  }

  // Check for Date object (including epoch date)
  if (value instanceof Date) {
    // Check if it's a valid date
    if (isNaN(value.getTime())) {
      return 'Not Set';
    }
    // Check for epoch date (1970) or any date before 2000
    if (value.getFullYear() < 2000) {
      return 'Not Set';
    }
    return moment(value).format('YYYY-MM-DD');
  }

  // Handle string dates that might represent epoch
  if (typeof value === 'string') {
    // Check for "Thu Jan 01 1970" pattern in the string
    if (value.includes('Jan 01 1970') || value.includes('Jan 1 1970') || value.includes('1970-01-01')) {
      return 'Not Set';
    }
    // Check if it's a date string in YYYY-MM-DD format
    const dateMatch = value.match(/^(\d{4})-\d{2}-\d{2}$/);
    if (dateMatch) {
      const year = parseInt(dateMatch[1]);
      if (year < 2000) {
        return 'Not Set';
      }
    }
    return value;
  }

  if (typeof value === 'object') {
    try {
      // Handle moment objects
      if (value && typeof value.format === 'function') {
        const year = value.year();
        if (year < 2000) {
          return 'Not Set';
        }
        return value.format('YYYY-MM-DD');
      }
      // Don't stringify complex objects, return a placeholder
      if (value._id || value.subworkstreamName || value.workstreamName) {
        return value.name || value.subworkstreamName || value.workstreamName || 'Object';
      }
      return JSON.stringify(value);
    } catch (e) {
      return 'Invalid Object';
    }
  }

  return String(value);
};

// Helper function to format numbers appropriately based on the metric
const formatMetricValue = (value, metricKey) => {
  if (value === undefined || value === null) return '0';

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '0';

  // List of metrics that should be integers (no decimals)
  const integerMetrics = [
    'initiativeCount',
    'totalMilestone',
    'totalMilestoneOwners',
    'totalLateMilestone',
    'completedMilestone',
    'workstreamActionCount',
    'workstreamActionOwnerCount',
    'lateWorkstreamAction',
    'initiativeOwnerCount',
    'initiativesPerOwner',
    'totalMilestonePerOwners',
    'totalMilestonePerInitiative',
    'averageWorkstreamActionPerOwner',
    'Number of Attachment',
    'totalActionItem'
  ];

  // List of metrics that should be formatted as amounts (with commas)
  const amountMetrics = [
    'netRecurringBenefitsPlanned',
    'oneTimeBenefitsPlanned',
    'implementationCostsPlanned',
    'netRecurringBenefitsLatest',
    'oneTimeBenefitsLatest',
    'implementationCostsLatest',
    'plannedImpactTotal',
    'actualImpactTotal',
    'forecastImpactTotal',
    'Total impact (total, ETB mn)',
    'plannedFTPValue',
    'actualFTPValue',
    'plannedKPI',
    'plannedDepositKPI',
    'plannedLoanKPI',
    'plannedFinancingKPI',
    'plannedFCYKPI',
    'actualKPI',
    'actualDepositKPI',
    'actualLoanKPI',
    'actualFinancingKPI',
    'actualFCYKPI',
    'plannedImpactTotalExcl',
    'actualImpactTotalExcl',
    'forecastImpactTotalExcl',
    'leImpactTotalExcl',
    'leImpactTotal',
    'totalL1NetRecurringBenefit',
    'totalL2NetRecurringBenefit',
    'totalL3NetRecurringBenefit',
    'totalL4NetRecurringBenefit',
    'totalL5NetRecurringBenefit',
    'totalL0L2NetRecurringBenefit',
    'totalL1PlusNetRecurringBenefit',
    'totalL2PlusNetRecurringBenefit',
    'totalL3PlusNetRecurringBenefit',
    'totalL4PlusNetRecurringBenefit',
    'totalL1L5NetRecurringBenefit',
    'ΔL1+vsLWL1+netrecurringbenefits',
    '(LW)L1+netrecurringbenefits'
  ];
  // Check if this metric should be an integer
  const shouldBeInteger = integerMetrics.some(metric =>
    metricKey?.includes(metric) || metricKey === metric
  );

  // Check if this metric should be formatted as an amount (with commas)
  const shouldBeAmount = amountMetrics.some(metric =>
    metricKey?.includes(metric) || metricKey === metric
  );

  let formattedValue;

  if (shouldBeInteger) {
    formattedValue = Math.round(numValue).toString();
  } else if (shouldBeAmount) {
    // Format with 3 decimal places and add commas
    formattedValue = numValue.toFixed(3);
    // Split into integer and decimal parts
    const [integerPart, decimalPart] = formattedValue.split('.');
    // Add commas to integer part
    const integerWithCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    formattedValue = decimalPart ? `${integerWithCommas}.${decimalPart}` : integerWithCommas;
  } else {
    // For other metrics, show 3 decimal places without commas
    formattedValue = numValue.toFixed(3);
  }

  return formattedValue;
};

// Helper Functions - These were part of your original code structure

const getCancellationDate = (initiative) => {
  const historyEntry = initiative.history?.find(
    (entry) =>
      entry.action?.includes('Weekly Status was changed from') &&
      entry.action?.includes('to Cancelled')
  );
  const dateValue = historyEntry?.timestamp ? new Date(historyEntry.timestamp) : null;
  return safeStringify(dateValue);
};

const getQualitativeKPI = (initiative) => {
  const kpi = initiative.KPI?.find((k) => k.isFinancial);
  return kpi ? 'No' : 'Yes';
};

const getImpactDataKey = (attr) => {
  if (attr.includes('planned')) return 'plan';
  if (attr.includes('actual')) return 'actual';
  if (attr.includes('forecast')) return 'forecast';
  if (attr.includes('le')) return 'estimate';
  return null;
};

const fiscalYears = ['FY 2023/24', 'FY 2024/25', 'FY 2025/26', 'FY 2026/27', 'FY 2027/28'];
const getQuarters = (year) => [`${year} Q1`, `${year} Q2`, `${year} Q3`, `${year} Q4`];
const getMonths = (year) => {
  const yearPart = year.split(' ')[1];
  const startYear = parseInt(yearPart.split('/')[0]);
  const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const fyMonths = [];
  let currentYear = startYear;
  let monthIndex = 0;
  for (let i = 0; i < 12; i++) {
    const yearSuffix = currentYear + (monthIndex > 5 ? 1 : 0);
    fyMonths.push(`${months[monthIndex]}-${yearSuffix}`);
    monthIndex++;
  }
  return fyMonths;
};

// New helper to determine which FYs are in the selected date range
const getFiscalYearsFromRange = (startDate, endDate) => {
  const yearsInDateRange = [];
  const startYear = startDate.getFullYear();
  let fyStartYear = startDate.getMonth() >= 6 ? startYear : startYear - 1; // Fiscal year starts in July (index 6)
  let fyEndYear = fyStartYear + 1;

  while (new Date(fyStartYear, 6, 1) <= endDate) {
    const fiscalYearString = `FY ${fyStartYear}/${String(fyEndYear).substring(2)}`;
    yearsInDateRange.push(fiscalYearString);
    fyStartYear++;
    fyEndYear++;
  }
  return yearsInDateRange.filter(fy => fiscalYears.includes(fy));
};

// Helper to check if a month/quarter is within the selected date range
const isPeriodInRange = (period, group, startDate, endDate) => {
  if (!startDate || !endDate) return true; // No date range selected, so all periods are valid

  let periodStart, periodEnd;
  const yearRegex = /FY (\d{4})/;
  const quarterRegex = /Q(\d)/;

  if (group === 'year') {
    const yearMatch = period.match(yearRegex);
    if (!yearMatch) return false;
    const startYear = parseInt(yearMatch[1]);
    periodStart = new Date(startYear, 6, 1);
    periodEnd = new Date(startYear + 1, 5, 30);
  } else if (group === 'quarter') {
    const yearMatch = period.match(yearRegex);
    const quarterMatch = period.match(quarterRegex);

    if (!yearMatch || !quarterMatch) return false;

    const startYear = parseInt(yearMatch[1]);
    const quarter = parseInt(quarterMatch[1]);

    switch (quarter) {
      case 1:
        periodStart = new Date(startYear, 6, 1);
        periodEnd = new Date(startYear, 8, 30);
        break;
      case 2:
        periodStart = new Date(startYear, 9, 1);
        periodEnd = new Date(startYear, 11, 31);
        break;
      case 3:
        periodStart = new Date(startYear + 1, 0, 1);
        periodEnd = new Date(startYear + 1, 2, 31);
        break;
      case 4:
        periodStart = new Date(startYear + 1, 3, 1);
        periodEnd = new Date(startYear + 1, 5, 30);
        break;
      default:
        return false;
    }
  } else if (group === 'month') {
    // Month format: 'MMM-YYYY' e.g., 'Jul-2024'
    const [month, year] = period.split('-');
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(month);
    const fullYear = parseInt(year);

    periodStart = new Date(fullYear, monthIndex, 1);
    periodEnd = new Date(fullYear, monthIndex + 1, 0); // Day 0 of the next month is the last day of the current month
  } else {
    return true;
  }

  return (periodStart <= endDate && periodEnd >= startDate);
};

const getTitleForAttribute = (attr) => {
  switch (attr) {
    case 'initiativeCount':
      return 'Initiatives (#)';
    case 'netRecurringBenefitsPlanned':
      return 'Net Recurring Benefit (Planned, ETB Mn)';
    case 'oneTimeBenefitsPlanned':
      return 'One Time Benefit (Planned, ETB Mn)';
    case 'implementationCostsPlanned':
      return 'One Time Implementation Cost (Planned, ETB Mn)';
    case 'netRecurringBenefitsLatest':
      return 'Net Recurring Benefit (LE, ETB Mn)';
    case 'oneTimeBenefitsLatest':
      return 'One Time Benefit (LE, ETB Mn)';
    case 'implementationCostsLatest':
      return 'Implementation Cost (LE, ETB Mn)';
    case 'plannedImpactTotal':
      return 'Planned Impact (Total, ETB Mn)';
    case 'actualImpactTotal':
      return 'Actual Impact (Total, ETB Mn)';
    case 'forecastImpactTotal':
      return 'Forecast Impact (Total, ETB Mn)';
    case 'Total impact (total, ETB mn':
      return 'Total Impact (Total, ETB Mn)';
    case 'plannedFTPValue':
      return 'Planned FTP Impact';
    case 'actualFTPValue':
      return 'Actual FTP Impact'
    case 'plannedKPI':
      return 'Total Planned KPI'
    case 'plannedDepositKPI':
      return 'Total Planned Deposit KPI';
    case 'plannedLoanKPI':
      return 'Total Planned Loan KPI';
    case 'plannedFinancingKPI':
      return 'Total Planned Financing KPI';
    case 'plannedFCYKPI':
      return 'Total Planned FCY KPI'
    case 'plannedAccount':
      return 'Total Account Planned to Open';
    case 'actualAccount':
      return 'Total Opened Account';
    case 'actualKPI':
      return 'Total Actual KPI';
    case 'actualDepositKPI':
      return 'Total Actual Deposit KPI';
    case 'actualLoanKPI':
      return 'Total Actual Loan KPI';
    case 'actualFinancingKPI':
      return 'Total Actual Financing KPI';
    case 'actualFCYKPI':
      return 'Total Actual FCY KPI';
    case 'plannedImpactTotalExcl':
      return 'Planned Impact Excl (Total, ETB Mn)';
    case 'actualImpactTotalExcl':
      return 'Actual Impact Excl (Total, ETB Mn)';
    case 'forecastImpactTotalExcl':
      return 'Forecast Impact Excl (Total, ETB Mn)';
    case 'leImpactTotalExcl':
      return 'LE Impact Excl (Total, ETB Mn)';
    case 'leImpactTotal':
      return 'LE Impact (Total, ETB Mn)';
    case 'initiativeOwnerCount':
      return 'Initiative Owners (#)';
    case 'initiativesPerOwner':
      return 'Initiative per owner (avg, #)';
    case 'totalL1NetRecurringBenefit':
      return 'Total L1 net recurring benefits (LE, ETB Mn)';
    case 'totalL2NetRecurringBenefit':
      return 'Total L2 net recurring benefits (LE, ETB Mn)';
    case 'totalL3NetRecurringBenefit':
      return 'Total L3 net recurring benefits (LE, ETB Mn)';
    case 'totalL4NetRecurringBenefit':
      return 'Total L4 net recurring benefits (LE, ETB Mn)';
    case 'totalL5NetRecurringBenefit':
      return 'Total L5 net recurring benefits (LE, ETB Mn)';
    case 'totalL0L2NetRecurringBenefit':
      return 'L0-L2 net recurring benefits (LE, ETB Mn)';
    case 'totalL1PlusNetRecurringBenefit':
      return 'L1+ net recurring benefits (LE, ETB Mn)';
    case 'totalL2PlusNetRecurringBenefit':
      return 'L2+ net recurring benefits (LE, ETB Mn)';
    case 'totalL3PlusNetRecurringBenefit':
      return 'L3+ net recurring benefits (LE, ETB Mn)';
    case 'totalL4PlusNetRecurringBenefit':
      return 'L4+ net recurring benefits (LE, ETB Mn)';
    case 'totalL1L5NetRecurringBenefit':
      return 'Total L1-L5 net recurring benefits (LE, ETB Mn)';
    case 'totalMilestone':
      return 'Total Milestones (#)';
    case 'totalMilestoneOwners':
      return 'Milestone Owners (#)';
    case 'totalMilestonePerOwners':
      return 'Milestones Per Owner (Avg)';
    case 'totalMilestonePerInitiative':
      return 'Milestones Per Initiative (Avg)';
    case 'totalLateMilestone':
      return 'Late Milestones (#)';
    case 'propertionOfLateMilestones':
      return 'Proportion of Late Milestones (%)';
    case 'completedMilestone':
      return 'Proportion of Completed Milestones (%)';
    case 'workstreamActionCount':
      return 'Workstream Actions (#)';
    case 'workstreamActionOwnerCount':
      return 'Action Item Owners (#)';
    case 'averageWorkstreamActionPerOwner':
      return 'Workstream Actions Per Owner (Avg)';
    case 'lateWorkstreamAction':
      return 'Late Workstream Actions (#)';
    case 'proportionOfLateWorkstreamActions':
      return 'Proportion of Late Workstream Actions (%)';
    case 'proportionOfCompletedWorkstreamActions':
      return 'Proportion of Completed Workstream Actions (%)';
    case 'ΔL1+vsLWL1+netrecurringbenefits':
      return 'Δ L1+ vs LW L1+ net recurring benefits (ETB Mn)';
    case '(LW)L1+netrecurringbenefits':
      return '(LW) L1+ net recurring benefits (LE, ETB Mn)';
    default:
      return attr;
  }
};

const getCalculationFunction = (attr, selectedImpactGroup) => {
  const { currentMonthIndex } = calculateCurrentIndexes();
  if (selectedImpactGroup) {
    const dataKey = getImpactDataKey(attr);
    if (!dataKey) return () => 0;

    return (items) => {
      let sum = 0;
      items.forEach(item => {
        const { impactItem, periodIndex } = item;
        let value = 0;
        if (selectedImpactGroup === 'year' && impactItem.annuallyPlan && dataKey === 'plan') {
          value = impactItem.annuallyPlan[periodIndex];
        } else if (selectedImpactGroup === 'quarter' && impactItem.quarterlyPlan && dataKey === 'plan') {
          value = impactItem.quarterlyPlan[periodIndex];
        } else if (selectedImpactGroup === 'month' && impactItem.monthlyPlan && dataKey === 'plan') {
          value = impactItem.monthlyPlan[periodIndex];
        }
        if (selectedImpactGroup === 'year' && impactItem.annuallyActual && dataKey === 'actual') {
          value = impactItem.annuallyActual[periodIndex];
        } else if (selectedImpactGroup === 'quarter' && impactItem.quarterlyActual && dataKey === 'actual') {
          value = impactItem.quarterlyActual[periodIndex];
        } else if (selectedImpactGroup === 'month' && impactItem.monthlyActual && dataKey === 'actual') {
          value = impactItem.monthlyActual[periodIndex];
        }
        if (value !== undefined) {
          sum += value;
        }
      });
      return sum;
    };
  } else {
    switch (attr) {
      case 'netRecurringBenefitsPlanned':
        return calculateTotalNetRecurringBenefit;
      case 'oneTimeBenefitsPlanned':
        return calculateTotalOneTimeBenefit;
      case 'implementationCostsPlanned':
        return calculateTotalImplementationCost;
      case 'netRecurringBenefitsLatest':
        return (items) => calculateTotalNetRecurringBenefitsLatest(items);
      case 'oneTimeBenefitsLatest':
        return (items) => calculateTotalOneTimeBenefitsLatest(items);
      case 'implementationCostsLatest':
        return (items) => calculateTotalImplementationCostLatest(items, currentMonthIndex);
      case 'plannedImpactTotal':
        return calculateTotalPlannedImpact;
      case 'actualImpactTotal':
        return calculateTotalActualImpact;
      case 'forecastImpactTotal':
        return calculateTotalPlannedImpact;
      case 'Total impact (total, ETB mn':
        return (items) => calculateTotalImpact(items);
      case 'plannedImpactTotalExcl':
        return calculateTotalPlannedImpactExcl;
      case 'actualImpactTotalExcl':
        return calculateTotalActualImpactExcl;
      case 'forecastImpactTotalExcl':
        return calculateTotalPlannedImpactExcl;
      case 'leImpactTotalExcl':
        return (items) => calculateTotalLatestEstimateImpact(items);
      case 'leImpactTotal':
        return (items) => calculateTotalLatestEstimateImpactInc(items);
      case 'initiativeCount':
        return (items) => items.length;
      case 'initiativeOwnerCount':
        return calculateTotalInitiativeOwners;
      case 'initiativesPerOwner':
        return perOwnerCount;
      case 'totalL1NetRecurringBenefit':
        return (items) => calculateStageNetRecurringBenefitsLatest(items, 'L1');
      case 'totalL2NetRecurringBenefit':
        return (items) => calculateStageNetRecurringBenefitsLatest(items, 'L2');
      case 'totalL3NetRecurringBenefit':
        return (items) => calculateStageNetRecurringBenefitsLatest(items, 'L3');
      case 'totalL4NetRecurringBenefit':
        return (items) => calculateStageNetRecurringBenefitsLatest(items, 'L4');
      case 'totalL5NetRecurringBenefit':
        return (items) => calculateStageNetRecurringBenefitsLatest(items, 'L5');
      case 'totalL0L2NetRecurringBenefit':
        return (items) => calculateStagesTotalNetRecurringBenefitsLatest(items, ['L0', 'L1', 'L2']);
      case 'totalL1PlusNetRecurringBenefit':
      case 'totalL1L5NetRecurringBenefit':
        return (items) => calculateStagesTotalNetRecurringBenefitsLatest(items, ['L1', 'L2', 'L3', 'L4', 'L5']);
      case 'totalL2PlusNetRecurringBenefit':
        return (items) => calculateStagesTotalNetRecurringBenefitsLatest(items, ['L2', 'L3', 'L4', 'L5']);
      case 'totalL3PlusNetRecurringBenefit':
        return (items) => calculateStagesTotalNetRecurringBenefitsLatest(items, ['L3', 'L4', 'L5']);
      case 'totalL4PlusNetRecurringBenefit':
        return (items) => calculateStagesTotalNetRecurringBenefitsLatest(items, ['L4', 'L5']);
      case 'totalMilestone':
        return (items) => calculateNumberOfMilestones(items);
      case 'totalMilestoneOwners':
        return (items) => countUniqueOwners(items);
      case 'totalMilestonePerOwners':
        return (items) => calculateMilestonesPerOwnerAverage(items);
      case 'totalMilestonePerInitiative':
        return (items) => calculateAverageMilestonesPerInitiative(items);
      case 'totalLateMilestone':
        return (items) => countLateMilestones(items);
      case 'propertionOfLateMilestones':
        return (items) => calculateLateMilestoneProportion(items);
      case 'completedMilestone':
        return (items) => calculateProportionOfCompletedMilestones(items);
      case 'workstreamActionCount':
        return (items) => calculateTotalActionItem(items);
      case 'workstreamActionOwnerCount':
        return (items) => calculateActionItemOwners(items);
      case 'averageWorkstreamActionPerOwner':
        return (items) => calculateActionItemPerOwnerAverage(items);
      case 'lateWorkstreamAction':
        return (items) => countLateWorkstreamActions(items);
      case 'proportionOfLateWorkstreamActions':
        return (items) => proportionLateWorkstreamActions(items);
      case 'proportionOfCompletedWorkstreamActions':
        return (items) => proportionCompletedWorkstreamActions(items);
      case 'ΔL1+vsLWL1+netrecurringbenefits':
        return (items) => calculateChangeNetRecurringBenefit(items);
      case '(LW)L1+netrecurringbenefits':
        return (items) => calculateLWRecurringBenefit(items);
      case 'plannedKPI':
        return (items) => calculateKPIPlan(items);
      case 'plannedDepositKPI':
        return (items) => calculateDepositKPIPlan(items);
      case 'plannedLoanKPI':
        return (items) => calculateLoanKPIPlan(items);
      case 'plannedFinancingKPI':
        return (items) => calculateFinancingKPIPlan(items);
      case 'plannedFCYKPI':
        return (items) => calculateFCYKPIPlan(items);
      case 'actualKPI':
        return (items) => calculateKPIActual(items);
      case 'plannedAccount':
        return (items) => calculateAccountKPIPlan(items);
      case 'actualAccount':
        return (items) => calculateAccountKPIActual(items);
      case 'actualDepositKPI':
        return (items) => calculateDepositKPIActual(items);
      case 'actualLoanKPI':
        return (items) => calculateLoanKPIActual(items);
      case 'actualFinancingKPI':
        return (items) => calculateFinancingKPIActual(items);
      case 'actualFCYKPI':
        return (items) => calculateFCYKPIActual(items);
      case 'plannedFTPValue':
        return (items) => calculatePlannedFTPImpact(items);
      case 'actualFTPValue':
        return (items) => calculateActualFTPImpact(items);
      default:
        return () => 'N/A';
    }
  }
};

function getDataKeyForAttribute(attr) {
  if (!attr || typeof attr !== 'string') return '';

  // 1. Handle FTP Values directly
  if (attr.startsWith('plannedFTPValue')) return 'plannedFTPValue';
  if (attr.startsWith('actualFTPValue')) return 'actualFTPValue';
  if (attr.startsWith('plannedDepositKPI')) return 'plannedDepositKPI';
  if (attr.startsWith('plannedLoanKPI')) return 'plannedLoanKPI';
  if (attr.startsWith('plannedFinancingKPI')) return 'plannedFinancingKPI';
  if (attr.startsWith('plannedFCYKPI')) return 'plannedFCYKPI';
  if (attr.startsWith('actualDepositKPI')) return 'actualDepositKPI';
  if (attr.startsWith('actualLoanKPI')) return 'actualLoanKPI';
  if (attr.startsWith('actualFinancingKPI')) return 'actualFinancingKPI';
  if (attr.startsWith('actualFCYKPI')) return 'actualFCYKPI';
  if (attr.startsWith('plannedAccount')) return 'plannedAccount';
  if (attr.startsWith('actualAccount')) return 'actualAccount';
  // 2. Handle Planned attributes
  if (attr.startsWith('plannedImpact') || attr === 'plannedKPI') {
    return attr === 'plannedKPI' ? 'plannedKPI' : 'planned';
  }

  // 3. Handle Latest Estimate (LE) attributes
  if (attr.startsWith('leImpact') || attr === 'leKPI') {
    return 'le';
  }

  // 4. Handle Actual attributes
  if (attr.startsWith('actualImpact') || attr === 'actualKPI') {
    return attr === 'actualKPI' ? 'actualKPI' : 'actual';
  }

  // 5. Default fallback
  return attr.toLowerCase();
}

const ListView = ({
  initiatives,
  actionItems,
  milestones,
  users,
  aggregationAttribute,
  colorByAttribute,
  initiativeColors,
  milestoneColors,
  actionItemColors,
  swapVisible,
  selectedMetric,
  sumVisible,
  tableDownloadVisible,
  setTableDownloadVisible,
  setCopyVisible,
  copyVisible,
  groupedImpact,
  selectedImpactGroup,
  setSelectedImpactGroup,
  groupedData,
  selectedGroup,
  selectedDateRange,
  isAllGroupsExpanded,
  setIsAllGroupsExpanded,
  sectors
}) => {

  const subworkstream = useWorkstreamData();
  const currentUser = useSelector(selectAuth);
  const { currentMonthIndex } = calculateCurrentIndexes();

  // Local state for managing expanded rows, now controlled by parent prop via useEffect
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Memoized filtered initiatives for better performance
  const myInitiatives = useMemo(() => {
    return initiatives && Array.isArray(initiatives) ?
      initiatives.filter((item) => {
        const isOwner = item.initiativeOwnerInfo?.[0]?._id === currentUser.id;
        const isBackupOwner = item.initiativeOwnerBackupInfo?.[0]?._id === currentUser.id;
        const isWorkstreamLead = item.workstreamLeadInfo?.[0]?._id === currentUser.id;
        const isWorkstreamSponsor = item.workstreamSponsorInfo?.[0]?._id === currentUser.id;
        const isAllowedUser = item.AllowOnlyTo?.some(
          (allowedUser) => allowedUser._id === currentUser.id,
        );
        const isAdminRole =
          currentUser.role.name === 'CTO' ||
          currentUser.role.name === 'TO Finance' ||
          currentUser.role.name === 'Strategy Analyst' ||
          currentUser.role.name === 'Admin';
        const hasAccess =
          isOwner ||
          isBackupOwner ||
          isWorkstreamLead ||
          isWorkstreamSponsor ||
          isAllowedUser ||
          isAdminRole;
        return hasAccess;
      }) :
      [];
  }, [initiatives, currentUser]);

  const myActionItems = useMemo(() => (actionItems && Array.isArray(actionItems) ? actionItems : []), [actionItems]);
  const myMilestones = useMemo(() => (milestones && Array.isArray(milestones) ? milestones : []), [milestones]);

  const historyMap = useMemo(() => {
    const map = new Map();
    myInitiatives.forEach((initiative) => {
      if (initiative.history) {
        initiative.history.forEach((entry) => {
          const milestoneMatch = entry.action?.match(/Milestone with Number (\d+)/);
          if (milestoneMatch) {
            const milestoneNumber = milestoneMatch[1];
            if (!map.has(milestoneNumber)) {
              map.set(milestoneNumber, []);
            }
            map.get(milestoneNumber).push(entry);
          }
        });
      }
    });
    return map;
  }, [myInitiatives]);

  // FIXED: getAttributeValue now returns strings, not objects
  const getAttributeValue = (item, attribute) => {
    switch (attribute) {
      case 'Stage':
      case 'Stage (simplified)':
        return determineStage(item);
      case 'Workstream Lead pending approval?':
        return checkWorkstreamLeadApprovalStatus(item);
      case 'Workstream Sponsor pending approval?':
        return checkWorkstreamSponsorApprovalStatus(item);
      case 'Finance lead pending approval?':
        return checkToFinanceApprovalStatus(item);
      case 'Strategy analyst pending approval?':
        return checkStrategyAnalystApprovalStatus(item);
      case 'workstreamSponsorWorkstream':
        const wsName = item.workstreamSponsorWorkstream?.[0]?.workstreamName;
        return wsName || '<not set>';
      case 'workstreamLeadSubworkstream':
        const subwsName = item.workstreamLeadSubworkstream?.[0]?.subworkstreamName;
        return subwsName || '<not set>';
      case 'crossFunctionalSupportWorkstreamInfo':
        const crossFunctionalName = item.crossFunctionalSupportWorkstreamInfo?.[0]?.subworkstreamName;
        return crossFunctionalName || '<not set>';
      case 'initiativeOwnerInfo':
        return `${item.initiativeOwnerInfo?.[0]?.firstName || ''} ${item.initiativeOwnerInfo?.[0]?.lastName || ''}`.trim() || '<not set>';
      case 'workstreamLeadInfo':
        return `${item.workstreamLeadInfo?.[0]?.firstName || ''} ${item.workstreamLeadInfo?.[0]?.lastName || ''}`.trim() || '<not set>';
      case 'workstreamSponsorInfo':
        return `${item.workstreamSponsorInfo?.[0]?.firstName || ''} ${item.workstreamSponsorInfo?.[0]?.lastName || ''}`.trim() || '<not set>';
      case 'initiativeOwnerBackupInfo':
        return `${item.initiativeOwnerBackupInfo?.[0]?.firstName || ''} ${item.initiativeOwnerBackupInfo?.[0]?.lastName || ''}`.trim() || '<not set>';
      case 'AllowOnlyTo':
        return item.AllowOnlyTo?.map((access) => `${access.firstName} ${access.lastName}`).join(',') || '<not set>';
      case 'Net recurring benefits range (latest estimate, annualized)':
        return getNetRecurringBenefitCategory(item);
      case 'Future implementation costs range':
        return getImplementationCostCatagory(item);
      case 'Pipeline weekly variance (L0+)':
        return getNetRecurringBenefitChangeCategoryL0Plus(item);
      case 'Pipeline weekly variance (L1+)':
        return getNetRecurringBenefitChangeCategoryL1Plus(item);
      case 'Pipeline weekly variance (L2+)':
        return getNetRecurringBenefitChangeCategoryL2Plus(item);
      case 'Pipeline weekly variance (L3+)':
        return getNetRecurringBenefitChangeCategoryL3Plus(item);
      case 'Pipeline weekly variance (L4+)':
        return getNetRecurringBenefitChangeCategoryL4Plus(item);
      case 'planningSummary':
      case 'Planning summary (simplified)':
        return InitiativePlanningSummary(item).planningStatus;
      case 'initiativeNumber - initiativeName':
        return `${item.initiativeNumber} - ${item.initiativeName}`;
      case 'L3 date compliance check (forecast vs planned date)':
        return getComplianceStatus(item, 'L3');
      case 'L4 date compliance check (forecast vs planned date)':
        return getComplianceStatus(item, 'L4');
      case 'Actual impact update check (for last completed and previous periods)':
        return sortInitiativesByActualImpact(item, attribute);
      case '(FY23-24) Actual impact update check':
      case '(FY24-25) Actual impact update check':
      case 'actualImpactUpdateCheck':
        return sortInitiativesByActuals(item, attribute);
      case 'actualKpiUpdateCheck':
        return sortInitiativesByActuals(item, attribute);
      case 'Actual KPI update check (for last completed and previous periods)':
        return sortInitiativesByActualImpact(item, attribute);
      case 'l2LatestEstimatedDate':
      case 'l2PlannedDate':
      case 'l2ForecastDate':
        return safeStringify(L2PlanDate(item));
      case 'l3PlannedDate':
      case 'l3ForecastDate':
        return safeStringify(L3PlanDate(item));
      case 'subForL5ApprovDate':
        return safeStringify(SubmittedForL1PlanDate(item));
      case 'subForL1ApprovDate':
        return safeStringify(SubmittedForL2PlanDate(item));
      case 'subForL4ApprovDate':
        return safeStringify(SubmittedForL3PlanDate(item));
      case 'subForL3ApprovDate':
        return safeStringify(SubmittedForL4PlanDate(item));
      case 'subForL2ApprovDate':
        return safeStringify(SubmittedForL5PlanDate(item));
      case 'cancellationDate':
        return getCancellationDate(item);
      case 'l4LatestEstimatedDate':
      case 'l4PlannedDate':
      case 'l4ForecastDate':
        return safeStringify(L4PlanDate(item));
      case 'l5LatestEstimatedDate':
      case 'l5PlannedDate':
      case 'l5ForecastDate':
        return safeStringify(L5PlanDate(item));
      case 'l0ActualDate':
        return safeStringify(L0PlanDate(item));
      case 'l1ActualDate':
        return safeStringify(L1PlanDate(item));
      case 'l2ActualDate':
        return safeStringify(L2ActualDate(item));
      case 'l3ActualDate':
        return safeStringify(L3ActualDate(item));
      case 'l4ActualDate':
        return safeStringify(L4ActualDate(item));
      case 'l5ActualDate':
        return safeStringify(L5ActualDate(item));
      case 'L1 Original Planned Date':
        return safeStringify(L1OriginalPlanDate(item));
      case 'L2 Original Planned Date':
        return safeStringify(L2OriginalPlanDate(item));
      case 'L3 Original Planned Date':
        return safeStringify(L3OriginalPlanDate(item));
      case 'L4 Original Planned Date':
        return safeStringify(L4OriginalPlanDate(item));
      case 'L5 Original Planned Date':
        return safeStringify(L5OriginalPlanDate(item));
      case 'latestActualDateAllLGates':
        return safeStringify(LLatestActualDate(item));
      case 'l1PlanYear': return getStagePlanYear(item, 'L1') || 'Not Set';
      case 'l1PlanQuarter': return getStagePlanQuarter(item, 'L1') || 'Not Set';
      case 'l1PlanMonth': return getStagePlanMonth(item, 'L1') || 'Not Set';
      case 'l1PlanWeek': return getStagePlanWeek(item, 'L1') || 'Not Set';
      case 'l2PlanYear': return getStagePlanYear(item, 'L2') || 'Not Set';
      case 'l2PlanQuarter': return getStagePlanQuarter(item, 'L2') || 'Not Set';
      case 'l2PlanMonth': return getStagePlanMonth(item, 'L2') || 'Not Set';
      case 'l2PlanWeek': return getStagePlanWeek(item, 'L2') || 'Not Set';
      case 'l3PlanYear': return getStagePlanYear(item, 'L3') || 'Not Set';
      case 'l3PlanQuarter': return getStagePlanQuarter(item, 'L3') || 'Not Set';
      case 'l3PlanMonth': return getStagePlanMonth(item, 'L3') || 'Not Set';
      case 'l3PlanWeek': return getStagePlanWeek(item, 'L3') || 'Not Set';
      case 'l4PlanYear': return getStagePlanYear(item, 'L4') || 'Not Set';
      case 'l4PlanQuarter': return getStagePlanQuarter(item, 'L4') || 'Not Set';
      case 'l4PlanMonth': return getStagePlanMonth(item, 'L4') || 'Not Set';
      case 'l4PlanWeek': return getStagePlanWeek(item, 'L4') || 'Not Set';
      case 'l5PlanYear': return getStagePlanYear(item, 'L5') || 'Not Set';
      case 'l5PlanQuarter': return getStagePlanQuarter(item, 'L5') || 'Not Set';
      case 'l5PlanMonth': return getStagePlanMonth(item, 'L5') || 'Not Set';
      case 'l5PlanWeek': return getStagePlanWeek(item, 'L5') || 'Not Set';

      // Fiscal period attributes for Initiatives - Actual Dates
      case 'l0ActualYear': return getStageActualYear(item, 'L0') || 'Not Set';
      case 'l0ActualQuarter': return getStageActualQuarter(item, 'L0') || 'Not Set';
      case 'l0ActualMonth': return getStageActualMonth(item, 'L0') || 'Not Set';
      case 'l0ActualWeek': return getStageActualWeek(item, 'L0') || 'Not Set';
      case 'l1ActualYear': return getStageActualYear(item, 'L1') || 'Not Set';
      case 'l1ActualQuarter': return getStageActualQuarter(item, 'L1') || 'Not Set';
      case 'l1ActualMonth': return getStageActualMonth(item, 'L1') || 'Not Set';
      case 'l1ActualWeek': return getStageActualWeek(item, 'L1') || 'Not Set';
      case 'l2ActualYear': return getStageActualYear(item, 'L2') || 'Not Set';
      case 'l2ActualQuarter': return getStageActualQuarter(item, 'L2') || 'Not Set';
      case 'l2ActualMonth': return getStageActualMonth(item, 'L2') || 'Not Set';
      case 'l2ActualWeek': return getStageActualWeek(item, 'L2') || 'Not Set';
      case 'l3ActualYear': return getStageActualYear(item, 'L3') || 'Not Set';
      case 'l3ActualQuarter': return getStageActualQuarter(item, 'L3') || 'Not Set';
      case 'l3ActualMonth': return getStageActualMonth(item, 'L3') || 'Not Set';
      case 'l3ActualWeek': return getStageActualWeek(item, 'L3') || 'Not Set';
      case 'l4ActualYear': return getStageActualYear(item, 'L4') || 'Not Set';
      case 'l4ActualQuarter': return getStageActualQuarter(item, 'L4') || 'Not Set';
      case 'l4ActualMonth': return getStageActualMonth(item, 'L4') || 'Not Set';
      case 'l4ActualWeek': return getStageActualWeek(item, 'L4') || 'Not Set';
      case 'l5ActualYear': return getStageActualYear(item, 'L5') || 'Not Set';
      case 'l5ActualQuarter': return getStageActualQuarter(item, 'L5') || 'Not Set';
      case 'l5ActualMonth': return getStageActualMonth(item, 'L5') || 'Not Set';
      case 'l5ActualWeek': return getStageActualWeek(item, 'L5') || 'Not Set';

      // Fiscal period attributes for Initiatives - Original Planned Dates
      case 'L1 Original Plan Year': return getStageOriginalPlanYear(item, 'L1') || 'Not Set';
      case 'L1 Original Plan Quarter': return getStageOriginalPlanQuarter(item, 'L1') || 'Not Set';
      case 'L1 Original Plan Month': return getStageOriginalPlanMonth(item, 'L1') || 'Not Set';
      case 'L1 Original Plan Week': return getStageOriginalPlanWeek(item, 'L1') || 'Not Set';
      case 'L2 Original Plan Year': return getStageOriginalPlanYear(item, 'L2') || 'Not Set';
      case 'L2 Original Plan Quarter': return getStageOriginalPlanQuarter(item, 'L2') || 'Not Set';
      case 'L2 Original Plan Month': return getStageOriginalPlanMonth(item, 'L2') || 'Not Set';
      case 'L2 Original Plan Week': return getStageOriginalPlanWeek(item, 'L2') || 'Not Set';
      case 'L3 Original Plan Year': return getStageOriginalPlanYear(item, 'L3') || 'Not Set';
      case 'L3 Original Plan Quarter': return getStageOriginalPlanQuarter(item, 'L3') || 'Not Set';
      case 'L3 Original Plan Month': return getStageOriginalPlanMonth(item, 'L3') || 'Not Set';
      case 'L3 Original Plan Week': return getStageOriginalPlanWeek(item, 'L3') || 'Not Set';
      case 'L4 Original Plan Year': return getStageOriginalPlanYear(item, 'L4') || 'Not Set';
      case 'L4 Original Plan Quarter': return getStageOriginalPlanQuarter(item, 'L4') || 'Not Set';
      case 'L4 Original Plan Month': return getStageOriginalPlanMonth(item, 'L4') || 'Not Set';
      case 'L4 Original Plan Week': return getStageOriginalPlanWeek(item, 'L4') || 'Not Set';
      case 'L5 Original Plan Year': return getStageOriginalPlanYear(item, 'L5') || 'Not Set';
      case 'L5 Original Plan Quarter': return getStageOriginalPlanQuarter(item, 'L5') || 'Not Set';
      case 'L5 Original Plan Month': return getStageOriginalPlanMonth(item, 'L5') || 'Not Set';
      case 'L5 Original Plan Week': return getStageOriginalPlanWeek(item, 'L5') || 'Not Set';

      // Fiscal period attributes for Initiatives - Cancellation
      case 'cancellationYear': return getCancellationYear(item) || 'Not Set';
      case 'cancellationQuarter': return getCancellationQuarter(item) || 'Not Set';
      case 'cancellationMonth': return getCancellationMonth(item) || 'Not Set';
      case 'cancellationWeek': return getCancellationWeek(item) || 'Not Set';

      // Fiscal period attributes for Action Items
      case 'actionItemCreatedOnYear': return getActionItemCreatedOnYear(item) || 'Not Set';
      case 'actionItemCreatedOnQuarter': return getActionItemCreatedOnQuarter(item) || 'Not Set';
      case 'actionItemCreatedOnMonth': return getActionItemCreatedOnMonth(item) || 'Not Set';
      case 'actionItemCreatedOnWeek': return getActionItemCreatedOnWeek(item) || 'Not Set';
      case 'actionItemUpdatedOnYear': return getActionItemUpdatedOnYear(item) || 'Not Set';
      case 'actionItemUpdatedOnQuarter': return getActionItemUpdatedOnQuarter(item) || 'Not Set';
      case 'actionItemUpdatedOnMonth': return getActionItemUpdatedOnMonth(item) || 'Not Set';
      case 'actionItemUpdatedOnWeek': return getActionItemUpdatedOnWeek(item) || 'Not Set';

      // Fiscal period attributes for Milestones
      case 'milestoneStartYear': return getMilestoneStartYear(item) || 'Not Set';
      case 'milestoneStartQuarter': return getMilestoneStartQuarter(item) || 'Not Set';
      case 'milestoneStartMonth': return getMilestoneStartMonth(item) || 'Not Set';
      case 'milestoneStartWeek': return getMilestoneStartWeek(item) || 'Not Set';
      case 'milestoneEndYear': return getMilestoneEndYear(item) || 'Not Set';
      case 'milestoneEndQuarter': return getMilestoneEndQuarter(item) || 'Not Set';
      case 'milestoneEndMonth': return getMilestoneEndMonth(item) || 'Not Set';
      case 'milestoneEndWeek': return getMilestoneEndWeek(item) || 'Not Set';
      case 'milestoneCreatedOnYear': return getMilestoneCreatedOnYear(item, historyMap.get(item.milestoneNumber)) || 'Not Set';
      case 'milestoneCreatedOnQuarter': return getMilestoneCreatedOnQuarter(item, historyMap.get(item.milestoneNumber)) || 'Not Set';
      case 'milestoneCreatedOnMonth': return getMilestoneCreatedOnMonth(item, historyMap.get(item.milestoneNumber)) || 'Not Set';
      case 'milestoneCreatedOnWeek': return getMilestoneCreatedOnWeek(item, historyMap.get(item.milestoneNumber)) || 'Not Set';
      case 'milestoneUpdatedOnYear': return getMilestoneUpdatedOnYear(item, historyMap.get(item.milestoneNumber)) || 'Not Set';
      case 'milestoneUpdatedOnQuarter': return getMilestoneUpdatedOnQuarter(item, historyMap.get(item.milestoneNumber)) || 'Not Set';
      case 'milestoneUpdatedOnMonth': return getMilestoneUpdatedOnMonth(item, historyMap.get(item.milestoneNumber)) || 'Not Set';
      case 'milestoneUpdatedOnWeek': return getMilestoneUpdatedOnWeek(item, historyMap.get(item.milestoneNumber)) || 'Not Set';
      case 'hasFinancialImpact':
        return item.hasFinancialImpact ? 'Yes' : 'No';
      case 'Number of Attachment':
        return item.attachment?.length || 0;
      case 'Has qualitative KPI?':
        return getQualitativeKPI(item);
      case 'segmentalPBT':
        return item?.segmentalPBT === true ? 'Yes' : 'No';
      case 'forSpecificCustomer':
        return item.forSpecificCustomer ? 'Yes' : 'No';
      case 'selectedForToMeetingDescusion':
        return item.selectedForToMeetingDescusion ? 'Yes' : 'No';
      case 'frequencyAndDirectionBinned':
        return getFrequencyAndDirection(item);
      case 'sectorInfo':
        return getSector(item, sectors);
      case 'subsectorInfo':
        return getSubsector(item, sectors);
      case 'type':
        return item?.type || '<not set>';
      case 'size':
        return item?.size || '<not set>';
      case 'initiativeCategory':
        return item?.initiativeCategory || '<not set>';
      case 'segment':
        return item?.segment || '<not set>';
      case 'whosePandL':
        const subworkstreamCategories = getSubworkstreamCatagory(item, subworkstream);
        const displayName = subworkstreamCategories.find((cat) => cat !== '') || '<not set>';
        return displayName;
      case 'typeOfImpact':
        return getTypeOfImpact(item);
      case 'milestoneNumber - name':
        return `${item.milestoneNumber} - ${item.name}`;
      case 'Owner':
        const owner = users?.find((user) => user._id === item.Owner);
        return owner ? `${owner.firstName || ''} ${owner.lastName || ''}`.trim() : 'Not Assigned';
      case 'Work starting date':
        return item.startDate ? moment(item.startDate).format('YYYY-MM-DD') : 'No Date';
      case 'Milestone completion date':
        return item.endDate ? moment(item.endDate).format('YYYY-MM-DD') : 'No Date';
      case 'Change in Milestone completion date (binned, current vs last week)':
        const status = item.status;
        if (status === 'Completed') return 'Unchanged';
        const milestoneEndDate = item.endDate ? moment(item.endDate) : null;
        if (!milestoneEndDate) return '<not set>';
        const today = moment().startOf('day');
        const daysDifference = milestoneEndDate.diff(today, 'days');
        if (daysDifference < 0) return 'Accelerated';
        if (daysDifference === 0) return 'Unchanged';
        if (daysDifference <= 7) return 'Delayed (1 to 7 days)';
        if (daysDifference <= 14) return 'Delayed (8 to 14 days)';
        if (daysDifference <= 30) return 'Delayed (15 to 30 days)';
        return 'Delayed (more than 30 days)';
      case 'Created On':
      case 'Created by':
      case 'Updated by':
      case 'Updated on':
        const historyEntries = historyMap.get(item.milestoneNumber);
        if (!historyEntries) return 'N/A';
        const getHistoryUser = (entry) => users?.find((u) => u._id === entry.user);
        if (attribute === 'Created On') {
          const createdEntry = historyEntries.find((entry) => entry.action?.includes('created'));
          return createdEntry?.timestamp ? moment(createdEntry.timestamp).format('YYYY-MM-DD') : 'N/A';
        }
        if (attribute === 'Created by') {
          const createdEntry = historyEntries.find((entry) => entry.action?.includes('created'));
          const creator = getHistoryUser(createdEntry);
          return creator ? `${creator.firstName} ${creator.lastName}`.trim() : 'N/A';
        }
        if (attribute === 'Updated by') {
          const updatedEntry = historyEntries[historyEntries.length - 1];
          const updater = getHistoryUser(updatedEntry);
          return updater ? `${updater.firstName} ${updater.lastName}`.trim() : 'N/A';
        }
        if (attribute === 'Updated on') {
          const updatedEntry = historyEntries[historyEntries.length - 1];
          return updatedEntry?.timestamp ? moment(updatedEntry.timestamp).format('YYYY-MM-DD') : 'N/A';
        }
        return 'N/A';
      case 'Accountable Workstream':
        const workstreamName = item?.owner?.subworkstream?.workstream?.workstreamName;
        return workstreamName || '<not set>';
      case 'Created on':
        return item?.created ? moment(item.created).format('YYYY-MM-DD') : 'N/A';
      case 'createdBy':
        return `${item?.createdBy?.firstName || ''} ${item?.createdBy?.lastName || ''}`.trim() || 'N/A';
      case 'Updated on':
        const updatedEvent = item?.activity?.find((event) =>
          event.event?.includes(`Workstream action ${item.actionItemNumber} is updated`)
        );
        return updatedEvent?.eventDate ? moment(updatedEvent.eventDate).format('YYYY-MM-DD') : 'N/A';
      case 'UpdatedBy':
        const updatedEvents = item?.activity?.find((event) =>
          event.event?.includes(`Workstream action ${item?.actionItemNumber} is updated`)
        );
        const updatedOnUser = updatedEvents?.owner ? `${updatedEvents?.owner?.firstName || ''} ${updatedEvents?.owner?.lastName || ''}`.trim() : '<not set>';
        return updatedOnUser;
      default:
        const value = item[attribute];
        return safeStringify(value);
    }
  };
  const { dataSource, columns } = useMemo(() => {
    let internalDataSource = [];
    let internalColumns = [];
    let periodsList = [];

    const startDate = selectedDateRange?.[0] ? new Date(selectedDateRange[0]) : null;
    const endDate = selectedDateRange?.[1] ? new Date(selectedDateRange[1]) : null;
    if (selectedImpactGroup && (selectedImpactGroup === 'year' || selectedImpactGroup === 'quarter' || selectedImpactGroup === 'month')) {
      let relevantFiscalYears = fiscalYears;
      if (startDate && endDate) {
        relevantFiscalYears = getFiscalYearsFromRange(startDate, endDate);
      }

      periodsList = relevantFiscalYears.flatMap(year => {
        if (selectedImpactGroup === 'year') {
          return [year];
        } else if (selectedImpactGroup === 'quarter') {
          return getQuarters(year);
        } else if (selectedImpactGroup === 'month') {
          return getMonths(year);
        }
        return [];
      }).filter(period => isPeriodInRange(period, selectedImpactGroup, startDate, endDate));

      window.exportPeriodsList = periodsList; // For external access if needed

      const groupedDataByMetric = {};

      // Helper function to safely get and convert numeric values
      const getSafeNumber = (value) => {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
      };

      myInitiatives.forEach(initiative => {
        const metricValue = colorByAttribute && colorByAttribute !== 'None' ? getAttributeValue(initiative, colorByAttribute) || '<not set>' : 'All Initiatives';
        const color = initiativeColors?.[metricValue] || '#ccc';

        if (!groupedDataByMetric[metricValue]) {
          groupedDataByMetric[metricValue] = {
            color,
            periods: {},
          };
        }

        // Process Impact data
        const impact = initiative.impact || [];
        impact.forEach(singleImpact => {
          const isPositiveImpact = singleImpact.frequencyAndDirection === 'Recurring Benefit (ETB)' || singleImpact.frequencyAndDirection === 'One Time Benefit';
          const isNegativeImpact = singleImpact.frequencyAndDirection === 'Recurring Negative Impact (ETB)' || singleImpact.frequencyAndDirection === 'One implementation Cost (ETB)';
          if (isPositiveImpact || isNegativeImpact) {
            let plannedImpacts = [], leImpacts = [], actualImpacts = [], plannedFTP = [], actualFTP = [];
            let allPeriodsList = [];

            if (selectedImpactGroup === 'year') {
              plannedImpacts = singleImpact.annuallyPlan || [];
              leImpacts = singleImpact.annuallyEstimate || [];
              actualImpacts = singleImpact.annuallyActual || [];
              plannedFTP = singleImpact.annuallyPlan || [];
              actualFTP = singleImpact.annuallyActual || [];
              allPeriodsList = fiscalYears;
            } else if (selectedImpactGroup === 'quarter') {
              plannedImpacts = singleImpact.quarterlyPlan || [];
              leImpacts = singleImpact.quarterlyEstimate || [];
              actualImpacts = singleImpact.quarterlyActual || [];
              plannedFTP = singleImpact.quarterlyPlan || [];
              actualFTP = singleImpact.quarterlyActual || [];
              allPeriodsList = fiscalYears.flatMap(year => getQuarters(year));
            } else if (selectedImpactGroup === 'month') {
              plannedImpacts = singleImpact.monthlyPlan || [];
              leImpacts = singleImpact.monthlyEstimate || [];
              actualImpacts = singleImpact.monthlyActual || [];
              plannedFTP = singleImpact.monthlyPlan || [];
              actualFTP = singleImpact.monthlyActual || [];
              allPeriodsList = fiscalYears.flatMap(year => getMonths(year));
            }

            periodsList.forEach((periodName) => {
              if (!groupedDataByMetric[metricValue].periods[periodName]) {
                groupedDataByMetric[metricValue].periods[periodName] = {
                  planned: 0,
                  le: 0,
                  actual: 0,
                  plannedKPI: 0,
                  actualKPI: 0,
                  plannedFTPValue: 0,
                  actualFTPValue: 0,
                  plannedDepositKPI: 0,
                  plannedLoanKPI: 0,
                  plannedFinancingKPI: 0,
                  plannedFCYKPI: 0,
                  actualDepositKPI: 0,
                  actualLoanKPI: 0,
                  actualFinancingKPI: 0,
                  actualFCYKPI: 0,
                  plannedAccount: 0,
                  actualAccount: 0,
                };
              }
              const sign = isPositiveImpact ? 1 : -1;
              const periodIndex = allPeriodsList.indexOf(periodName);
              if (periodIndex !== -1) {
                // Use safe number conversion for all calculations
                groupedDataByMetric[metricValue].periods[periodName].planned += getSafeNumber(plannedImpacts[periodIndex]) * sign;
                groupedDataByMetric[metricValue].periods[periodName].le += getSafeNumber(leImpacts[periodIndex]) * sign;
                groupedDataByMetric[metricValue].periods[periodName].actual += getSafeNumber(actualImpacts[periodIndex]) * sign;
                groupedDataByMetric[metricValue].periods[periodName].plannedFTPValue += getSafeNumber(plannedFTP[periodIndex]) * sign;
                groupedDataByMetric[metricValue].periods[periodName].actualFTPValue += getSafeNumber(actualFTP[periodIndex]) * sign;
              }
            });
          }
        });

        // Process KPI data
        const KPI = initiative.KPI || [];
        KPI.forEach(singleKPI => {
          let plannedKPI = [], plannedDepositKPI = [], plannedLoanKPI = [], plannedFinancingKPI = [], plannedFCYKPI = [],
            actualKPI = [], actualDepositKPI = [], actualLoanKPI = [], actualFinancingKPI = [], actualFCYKPI = [], actualAccount = [], plannedAccount = [];
          let allPeriodsList = [];

          if (selectedImpactGroup === 'year') {
            plannedKPI = singleKPI.annuallyPlan || [];
            plannedLoanKPI = singleKPI.annuallyPlan || [];
            plannedDepositKPI = singleKPI.annuallyPlan || [];
            plannedFinancingKPI = singleKPI.annuallyPlan || [];
            plannedFCYKPI = singleKPI.annuallyPlan || [];
            plannedAccount = singleKPI.annuallyPlan || [];
            actualKPI = singleKPI.annuallyActual || [];
            actualDepositKPI = singleKPI.annuallyActual || [];
            actualLoanKPI = singleKPI.annuallyActual || [];
            actualFinancingKPI = singleKPI.annuallyActual || [];
            actualFCYKPI = singleKPI.annuallyActual || [];
            actualAccount = singleKPI.annuallyActual || [];
            allPeriodsList = fiscalYears;
          } else if (selectedImpactGroup === 'quarter') {
            plannedKPI = singleKPI.quarterlyPlan || [];
            plannedLoanKPI = singleKPI.quarterlyPlan || [];
            plannedDepositKPI = singleKPI.quarterlyPlan || [];
            plannedFinancingKPI = singleKPI.quarterlyPlan || [];
            plannedFCYKPI = singleKPI.quarterlyPlan || [];
            plannedAccount = singleKPI.quarterlyPlan || [];
            actualKPI = singleKPI.quarterlyActual || [];
            actualDepositKPI = singleKPI.quarterlyActual || [];
            actualLoanKPI = singleKPI.quarterlyActual || [];
            actualFinancingKPI = singleKPI.quarterlyActual || [];
            actualFCYKPI = singleKPI.quarterlyActual || [];
            actualAccount = singleKPI.quarterlyActual || [];
            allPeriodsList = fiscalYears.flatMap(year => getQuarters(year));
          } else if (selectedImpactGroup === 'month') {
            plannedKPI = singleKPI.monthlyPlan || [];
            plannedLoanKPI = singleKPI.monthlyPlan || [];
            plannedDepositKPI = singleKPI.monthlyPlan || [];
            plannedFinancingKPI = singleKPI.monthlyPlan || [];
            plannedFCYKPI = singleKPI.monthlyPlan || [];
            plannedAccount = singleKPI.monthlyPlan || [];
            actualKPI = singleKPI.monthlyActual || [];
            actualDepositKPI = singleKPI.monthlyActual || [];
            actualLoanKPI = singleKPI.monthlyActual || [];
            actualFinancingKPI = singleKPI.monthlyActual || [];
            actualFCYKPI = singleKPI.monthlyActual || [];
            actualAccount = singleKPI.monthlyActual || [];
            allPeriodsList = fiscalYears.flatMap(year => getMonths(year));
          }

          periodsList.forEach((periodName) => {
            if (!groupedDataByMetric[metricValue].periods[periodName]) {
              groupedDataByMetric[metricValue].periods[periodName] = {
                planned: 0,
                le: 0,
                actual: 0,
                plannedKPI: 0,
                plannedDepositKPI: 0,
                plannedLoanKPI: 0,
                plannedFinancingKPI: 0,
                plannedFCYKPI: 0,
                plannedAccount: 0,
                actualKPI: 0,
                actualDepositKPI: 0,
                actualLoanKPI: 0,
                actualFinancingKPI: 0,
                actualFCYKPI: 0,
                actualAccount: 0,
                plannedFTPValue: 0,
                actualFTPValue: 0,
              };
            }
            const periodIndex = allPeriodsList.indexOf(periodName);
            if (periodIndex !== -1) {
              // Use safe number conversion for all KPI calculations
              groupedDataByMetric[metricValue].periods[periodName].plannedKPI += getSafeNumber(plannedKPI[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].plannedDepositKPI += getSafeNumber(plannedDepositKPI[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].plannedLoanKPI += getSafeNumber(plannedLoanKPI[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].plannedFinancingKPI += getSafeNumber(plannedFinancingKPI[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].plannedFCYKPI += getSafeNumber(plannedFCYKPI[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].plannedAccount += getSafeNumber(plannedAccount[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].actualKPI += getSafeNumber(actualKPI[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].actualDepositKPI += getSafeNumber(actualDepositKPI[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].actualLoanKPI += getSafeNumber(actualLoanKPI[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].actualFinancingKPI += getSafeNumber(actualFinancingKPI[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].actualFCYKPI += getSafeNumber(actualFCYKPI[periodIndex]);
              groupedDataByMetric[metricValue].periods[periodName].actualAccount += getSafeNumber(actualAccount[periodIndex]);
            }
          });
        });
      });

      const validImpactAttributes = ['plannedImpactTotal', 'leImpactTotal', 'actualImpactTotal', 'plannedImpactTotalExcl', 'actualImpactTotalExcl', 'leImpactTotalExcl', 'plannedFTPValue', 'actualFTPValue'];
      const validKPIAttributes = ['plannedKPI', 'plannedDepositKPI', 'plannedLoanKPI', 'plannedFinancingKPI', 'plannedFCYKPI', 'plannedAccount', 'actualKPI', 'actualDepositKPI', 'actualLoanKPI', 'actualFinancingKPI', 'actualFCYKPI', 'actualAccount'];
      const selectedImpactAttributes = aggregationAttribute.filter(attr => validImpactAttributes.includes(attr));
      const selectedKPIAttributes = aggregationAttribute.filter(attr => validKPIAttributes.includes(attr));

      // Combine all selected attributes
      const allSelectedAttributes = [...selectedImpactAttributes, ...selectedKPIAttributes];

      // Helper function for formatting with proper integer/decimal handling
      const toFixedSafe = (value, metricKey) => {
        const num = getSafeNumber(value);
        return formatMetricValue(num, metricKey);
      };

      if (colorByAttribute && colorByAttribute !== 'None') {
        if (swapVisible) {
          internalDataSource = Object.keys(groupedDataByMetric).map(metricValue => {
            const metricGroup = groupedDataByMetric[metricValue];
            const parentRow = {
              key: metricValue,
              metric: metricValue,
              color: metricGroup.color,
            };
            parentRow.children = periodsList.map(period => {
              const childRow = {
                key: `${metricValue}_${period}`,
                metric: period,
                isChild: true,
                parentColor: metricGroup.color,
              };
              allSelectedAttributes.forEach(attr => {
                const dataKey = getDataKeyForAttribute(attr);
                childRow[attr] = toFixedSafe(metricGroup.periods[period]?.[dataKey], attr);
              });
              return childRow;
            });
            return parentRow;
          });

          internalColumns = [{
            title: getTitleForAttribute(colorByAttribute),
            dataIndex: 'metric',
            key: 'metric',
            fixed: 'left',
            width: 250,
            render: (text, record) => {
              if (record.isChild) {
                return (<div style={{ paddingLeft: 24 }}><span style={{ color: 'rgba(0,0,0,0.65)' }}>{text}</span></div>);
              }
              return (<div style={{ display: 'flex', alignItems: 'center' }}><div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: record.color, marginRight: 8, }} /><strong>{text}</strong></div>);
            },
          },
          ...allSelectedAttributes.map(attr => ({
            title: getTitleForAttribute(attr),
            dataIndex: attr,
            key: attr,
            render: (value, record) => {
              if (record.isChild) {
                const formattedValue = formatMetricValue(value, attr);
                return (<div style={{ backgroundColor: `${record.parentColor}20`, padding: '4px 8px', color: 'black' }}>{formattedValue}</div>);
              }
              const total = record.children?.reduce((sum, child) => sum + getSafeNumber(child[attr]), 0);
              return <strong>{toFixedSafe(total, attr)}</strong>;
            },
          })),
          ];

          if (sumVisible) {
            // When swapVisible is true and sumVisible is true, add Total as a parent row
            const totalRow = {
              key: 'total-row',
              metric: 'Total',
              isTotalRow: true,
              color: '#ccc',
              children: periodsList.map(period => {
                const childRow = {
                  key: `total_${period}`,
                  metric: period,
                  isChild: true,
                  parentColor: '#ccc',
                };
                allSelectedAttributes.forEach(attr => {
                  const total = internalDataSource.reduce((acc, parent) => {
                    if (parent.children) {
                      const periodChild = parent.children.find(child => child.metric === period);
                      if (periodChild) {
                        return acc + getSafeNumber(periodChild[attr]);
                      }
                    }
                    return acc;
                  }, 0);
                  childRow[attr] = toFixedSafe(total, attr);
                });
                return childRow;
              })
            };
            internalDataSource.push(totalRow);
          }
        } else { // Impact view, colorByAttribute selected, NOT swapped
          internalDataSource = Object.keys(groupedDataByMetric).map(metricValue => {
            const metricGroup = groupedDataByMetric[metricValue];
            const parentRow = {
              key: metricValue,
              metric: metricValue,
              color: metricGroup.color,
            };
            parentRow.children = allSelectedAttributes.map(attr => {
              const periodData = metricGroup.periods;
              const dataKey = getDataKeyForAttribute(attr);
              return {
                key: `${metricValue}_${attr}`,
                metric: getTitleForAttribute(attr),
                isChild: true,
                parentColor: metricGroup.color,
                ...Object.fromEntries(periodsList.map(period => [period, toFixedSafe(periodData[period]?.[dataKey], attr)])),
              };
            });
            return parentRow;
          });

          internalColumns = [{
            title: 'Metrics',
            dataIndex: 'metric',
            key: 'metric',
            fixed: 'left',
            width: 250,
            render: (text, record) => {
              if (record.isChild) {
                return (<div style={{ paddingLeft: 24 }}><span style={{ color: 'rgba(0,0,0,0.65)' }}>{text}</span></div>);
              }
              return (<div style={{ display: 'flex', alignItems: 'center' }}><div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: record.color, marginRight: 8, }} /><strong>{text}</strong></div>);
            }
          },
          ...periodsList.map(period => ({
            title: period,
            dataIndex: period,
            key: period,
            render: (value, record) => {
              if (record.isChild) {
                const formattedValue = formatMetricValue(value, record.metric);
                return (<div style={{ backgroundColor: `${record.parentColor}20`, padding: '4px 8px', color: 'black' }}>{formattedValue}</div>);
              } else {
                return ''; // Empty string for parent row
              }
            }
          })),
          ];

          if (sumVisible) {
            // When swapVisible is false and sumVisible is true, add Total as a column
            internalColumns.push({
              title: 'Total',
              dataIndex: 'Total',
              key: 'total',
              fixed: 'right',
              render: (value, record) => {
                if (record.isChild) {
                  const totalValue = periodsList.reduce((sum, period) => sum + getSafeNumber(record[period]), 0);
                  const formattedTotal = formatMetricValue(totalValue, record.metric);
                  return (<div style={{ backgroundColor: `${record.parentColor}20`, padding: '4px 8px', color: 'black' }}><strong>{formattedTotal}</strong></div>);
                } else {
                  return ''; // Empty string for parent row
                }
              }
            });
          }
        }
      } else { // Impact view, NO colorByAttribute selected
        if (swapVisible) {
          // Swapped view: transpose periods <-> metrics
          const tempGroupedData = {};

          periodsList.forEach(period => {
            tempGroupedData[period] = {
              key: period,
              metric: period, // period is now the row label
            };

            allSelectedAttributes.forEach(attr => {
              const allMetricsData = groupedDataByMetric['All Initiatives'];
              const dataKey = getDataKeyForAttribute(attr);
              const periodData = allMetricsData.periods;

              tempGroupedData[period][attr] = toFixedSafe(periodData[period]?.[dataKey], attr);
            });
          });

          internalDataSource = Object.values(tempGroupedData);

          internalColumns = [
            {
              title: 'Periods',
              dataIndex: 'metric',
              key: 'metric',
              fixed: 'left',
              width: 250,
              render: text => <strong>{text}</strong>,
            },
            ...allSelectedAttributes.map(attr => ({
              title: getTitleForAttribute(attr),
              dataIndex: attr,
              key: attr,
              render: (value, record) => {
                const formattedValue = formatMetricValue(value, attr);
                return formattedValue || '0';
              },
            })),
          ];

          if (sumVisible) {
            // New logic: When swapVisible is true and NO colorByAttribute, add Total as a row
            const totalRow = {
              key: 'total-row',
              metric: 'Total',
              color: '#ccc',
            };

            allSelectedAttributes.forEach(attr => {
              const totalValue = internalDataSource.reduce(
                (sum, record) => sum + getSafeNumber(record[attr]),
                0
              );
              totalRow[attr] = toFixedSafe(totalValue, attr);
            });
            internalDataSource.push(totalRow);
          }
        } else {
          // Original non-swapped logic for selectedImpactGroup only
          internalDataSource = allSelectedAttributes.map(attr => {
            const allMetricsData = groupedDataByMetric['All Initiatives'];
            const dataKey = getDataKeyForAttribute(attr);
            const periodData = allMetricsData.periods;

            return {
              key: attr,
              metric: getTitleForAttribute(attr),
              color: '#ccc',
              ...Object.fromEntries(
                periodsList.map(
                  period => [period, toFixedSafe(periodData[period]?.[dataKey], attr)]
                )
              ),
            };
          });
          internalColumns = [
            {
              title: 'Metrics',
              dataIndex: 'metric',
              key: 'metric',
              fixed: 'left',
              width: 250,
              render: text => <strong>{text}</strong>,
            },
            ...periodsList.map(period => ({
              title: period,
              dataIndex: period,
              key: period,
              render: (value, record) => {
                const formattedValue = formatMetricValue(value, record.metric);
                return formattedValue || '0';
              },
            })),
          ];
          if (sumVisible) {
            internalColumns.push({
              title: 'Total',
              dataIndex: 'Total',
              key: 'total',
              fixed: 'right',
              render: (value, record) => {
                const totalValue = periodsList.reduce(
                  (sum, period) => sum + getSafeNumber(record[period]),
                  0
                );
                return <strong>{toFixedSafe(totalValue, record.metric)}</strong>;
              },
            });
          }
        }
      }
    } else if (selectedGroup && selectedGroup !== 'None' && groupedData) {
      if (colorByAttribute && colorByAttribute !== 'None') {
        if (swapVisible) {
          // Swapped View: colorByAttribute is the parent, selectedGroup values are columns
          const tempGroupedData = {};
          const uniqueSubgroups = new Set();
          for (const groupName in groupedData) {
            if (Object.prototype.hasOwnProperty.call(groupedData, groupName)) {
              const initiativesInGroup = groupedData[groupName];
              initiativesInGroup.forEach(initiative => {
                const colorAttributeValue = getAttributeValue(initiative, colorByAttribute) || '<not set>';
                const selectedGroupValue = getAttributeValue(initiative, selectedGroup) || '<not set>';
                uniqueSubgroups.add(selectedGroupValue);
                if (!tempGroupedData[colorAttributeValue]) {
                  tempGroupedData[colorAttributeValue] = {
                    key: colorAttributeValue,
                    displayValue: colorAttributeValue,
                    color: initiativeColors?.[colorAttributeValue] || '#ccc',
                    children: {},
                  };
                }
                if (!tempGroupedData[colorAttributeValue].children[selectedGroupValue]) {
                  tempGroupedData[colorAttributeValue].children[selectedGroupValue] = {
                    initiativesInGroup: [],
                  };
                }
                tempGroupedData[colorAttributeValue].children[selectedGroupValue].initiativesInGroup.push(initiative);
              });
            }
          }

          internalDataSource = Object.values(tempGroupedData).map(parentGroup => {
            const children = aggregationAttribute.map(attr => {
              const childRow = {
                key: `${parentGroup.key}_${attr}`,
                metric: getTitleForAttribute(attr),
                isChild: true,
                parentColor: parentGroup.color,
              };
              Array.from(uniqueSubgroups).sort().forEach(subgroup => {
                const initiatives = parentGroup.children[subgroup]?.initiativesInGroup || [];
                const calcFunc = getCalculationFunction(attr, false);
                const rawValue = calcFunc(initiatives);
                childRow[subgroup] = formatMetricValue(rawValue, attr);
              });
              return childRow;
            });
            parentGroup.children = children;
            return parentGroup;
          });

          internalColumns = [
            {
              title: getTitleForAttribute(colorByAttribute),
              dataIndex: 'metric',
              key: 'group',
              fixed: 'left',
              width: 250,
              render: (text, record) => {
                if (record.isChild) {
                  return (
                    <div style={{ paddingLeft: 24 }}>
                      <span style={{ color: 'rgba(0,0,0,0.65)' }}>{text}</span>
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: record.color, marginRight: '8px' }} />
                    <strong>{record.displayValue}</strong>
                  </div>
                );
              },
            },
            ...Array.from(uniqueSubgroups).sort().map(subgroup => ({
              title: subgroup,
              dataIndex: subgroup,
              key: subgroup,
              render: (value, record) => {
                if (!record.isChild) return null;
                return value || '0';
              },
            })),
          ];

          if (sumVisible) {
            // When swapVisible is true, add Total as a column
            internalColumns.push({
              title: 'Total',
              dataIndex: 'total',
              key: 'total',
              fixed: 'right',
              render: (value, record) => {
                if (record.isChild) {
                  // Calculate row total for child rows
                  const rowTotal = Array.from(uniqueSubgroups).reduce((sum, subgroup) => {
                    return sum + (parseFloat(record[subgroup]) || 0);
                  }, 0);
                  return <strong>{formatMetricValue(rowTotal, 'total')}</strong>;
                }
                // Calculate row total for parent rows
                const rowTotal = Array.from(uniqueSubgroups).reduce((sum, subgroup) => {
                  const total = record.children?.reduce((childSum, child) => {
                    return childSum + (parseFloat(child[subgroup]) || 0);
                  }, 0) || 0;
                  return sum + total;
                }, 0);
                return <strong>{formatMetricValue(rowTotal, 'total')}</strong>;
              },
            });
          }
        } else { // Grouped by selectedGroup, then colored by colorByAttribute, NOT swapped
          const tempGroupedData = {};
          for (const groupName in groupedData) {
            if (Object.prototype.hasOwnProperty.call(groupedData, groupName)) {
              const initiativesInGroup = groupedData[groupName];
              initiativesInGroup.forEach(initiative => {
                const colorAttributeValue = getAttributeValue(initiative, colorByAttribute) || '<not set>';
                const subGroupKey = groupName; // The groupName from groupedData is the child here

                if (!tempGroupedData[colorAttributeValue]) {
                  tempGroupedData[colorAttributeValue] = {
                    key: colorAttributeValue,
                    displayValue: colorAttributeValue,
                    color: initiativeColors?.[colorAttributeValue] || '#ccc',
                    initiativesInGroup: [],
                    children: {},
                  };
                }
                if (!tempGroupedData[colorAttributeValue].children[subGroupKey]) {
                  tempGroupedData[colorAttributeValue].children[subGroupKey] = {
                    key: `${colorAttributeValue}_${subGroupKey}`,
                    displayValue: subGroupKey,
                    initiativesInGroup: [],
                  };
                }
                tempGroupedData[colorAttributeValue].children[subGroupKey].initiativesInGroup.push(initiative);
                tempGroupedData[colorAttributeValue].initiativesInGroup.push(initiative); // Also add to parent group for its total
              });
            }
          }

          internalDataSource = Object.values(tempGroupedData).map(parentGroup => {
            parentGroup.children = Object.values(parentGroup.children);
            return parentGroup;
          });

          internalDataSource.forEach(group => {
            aggregationAttribute.forEach(attr => {
              const calcFunc = getCalculationFunction(attr, false);
              let parentTotal = 0;
              group.children?.forEach(child => {
                const items = child.initiativesInGroup || [];
                const value = calcFunc(items);
                child[attr] = value;
                parentTotal += value;
              });
              group[attr] = parentTotal;
            });
          });

          internalColumns = [{
            title: getTitleForAttribute(colorByAttribute),
            dataIndex: 'displayValue',
            key: 'group',
            fixed: 'left',
            render: (text, record) => {
              if (record.children) {
                return (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: record.color, marginRight: '8px' }} />
                    <strong>{text}</strong>
                  </div>
                );
              } else {
                return <div style={{ paddingLeft: 24 }}>{text}</div>;
              }
            },
          }];
          internalColumns = internalColumns.concat(aggregationAttribute.map(attr => ({
            title: getTitleForAttribute(attr),
            dataIndex: attr,
            key: attr,
            render: (value, record) => {
              if (record.children) {
                return ''; // Empty string for parent row
              }
              return formatMetricValue(value || 0, attr);
            }
          })));

          if (sumVisible) {
            // When swapVisible is false, add Total as a bottom row
            const totalRow = {
              key: 'total-row',
              displayValue: 'Total',
              isTotalRow: true,
              color: '#ccc',
            };

            // Calculate column totals
            aggregationAttribute.forEach(attr => {
              const columnTotal = internalDataSource.reduce((sum, group) => {
                if (group.children) {
                  const groupTotal = group.children.reduce((childSum, child) => {
                    return childSum + (parseFloat(child[attr]) || 0);
                  }, 0);
                  return sum + groupTotal;
                }
                return sum;
              }, 0);
              totalRow[attr] = formatMetricValue(columnTotal, attr);
            });

            internalDataSource.push(totalRow);
          }
        }
      } else { // Grouped by selectedGroup, NO colorByAttribute selected
        if (swapVisible) {
          // Swapped view for selectedGroup only
          const tempGroupedData = {};
          const uniqueSubgroups = new Set();
          for (const groupName in groupedData) {
            if (Object.prototype.hasOwnProperty.call(groupedData, groupName)) {
              const initiativesInGroup = groupedData[groupName];
              uniqueSubgroups.add(groupName);
              aggregationAttribute.forEach(attr => {
                const calcFunc = getCalculationFunction(attr, false);
                const value = calcFunc(initiativesInGroup);
                if (!tempGroupedData[attr]) {
                  tempGroupedData[attr] = {
                    key: attr,
                    metric: getTitleForAttribute(attr),
                  };
                }
                tempGroupedData[attr][groupName] = value;
              });
            }
          }
          internalDataSource = Object.values(tempGroupedData).map(row => {
            const formattedRow = { key: row.key, metric: row.metric };
            Array.from(uniqueSubgroups).sort().forEach(subgroup => {
              formattedRow[subgroup] = formatMetricValue(row[subgroup] || 0, row.metric);
            });
            return formattedRow;
          });

          internalColumns = [
            {
              title: 'Metrics',
              dataIndex: 'metric',
              key: 'metric',
              fixed: 'left',
              width: 250,
              render: (text) => <strong>{text}</strong>
            },
            ...Array.from(uniqueSubgroups).sort().map(subgroup => ({
              title: subgroup,
              dataIndex: subgroup,
              key: subgroup,
              render: (value) => value || '0',
            }))
          ];
          if (sumVisible) {
            // Add total column for swapped view
            internalColumns.push({
              title: 'Total',
              dataIndex: 'total',
              key: 'total',
              fixed: 'right',
              render: (value, record) => {
                const rowTotal = Array.from(uniqueSubgroups).reduce((acc, subgroup) => acc + (parseFloat(record[subgroup]) || 0), 0);
                return <strong>{formatMetricValue(rowTotal, 'total')}</strong>;
              }
            });
          }
        } else {
          // Default non-swapped view for selectedGroup only
          internalDataSource = Object.keys(groupedData).map(groupName => ({
            key: groupName,
            displayValue: groupName,
            initiativesInGroup: groupedData[groupName],
          }));
          internalDataSource.forEach(group => {
            aggregationAttribute.forEach(attr => {
              const calcFunc = getCalculationFunction(attr, false);
              const items = group.initiativesInGroup || [];
              group[attr] = calcFunc(items);
            });
          });
          internalColumns = [{
            title: getTitleForAttribute(selectedGroup),
            dataIndex: 'displayValue',
            key: 'group',
            render: (text) => <b>{text}</b>,
          }];
          internalColumns = internalColumns.concat(aggregationAttribute.map(attr => ({
            title: getTitleForAttribute(attr),
            dataIndex: attr,
            key: attr,
            render: (value) => formatMetricValue(value || 0, attr),
          })));
          if (sumVisible) {
            // When swapVisible is false, add Total as a bottom row
            const totalRow = {
              key: 'total-row',
              displayValue: 'Total',
              isTotalRow: true,
            };

            // Calculate column totals
            aggregationAttribute.forEach(attr => {
              const columnTotal = internalDataSource.reduce((sum, group) => {
                return sum + (parseFloat(group[attr]) || 0);
              }, 0);
              totalRow[attr] = formatMetricValue(columnTotal, attr);
            });

            internalDataSource.push(totalRow);
          }
        }
      }

    } else { // No selectedGroup and no selectedImpactGroup
      if (colorByAttribute && colorByAttribute !== 'None') {
        if (swapVisible) {
          // Swapped view for colorByAttribute only
          const tempGroupedData = {};
          const uniqueSubgroups = new Set();
          myInitiatives.forEach(item => {
            const attributeValue = getAttributeValue(item, colorByAttribute) || '<not set>';
            uniqueSubgroups.add(attributeValue);
            aggregationAttribute.forEach(attr => {
              const calcFunc = getCalculationFunction(attr, false);
              const value = calcFunc([item]);
              if (!tempGroupedData[attr]) {
                tempGroupedData[attr] = {
                  key: attr,
                  metric: getTitleForAttribute(attr),
                };
              }
              if (!tempGroupedData[attr][attributeValue]) {
                tempGroupedData[attr][attributeValue] = 0;
              }
              tempGroupedData[attr][attributeValue] += value;
            });
          });
          // Handle milestones and action items separately for aggregation
          const itemsToAggregate = [...myMilestones, ...myActionItems];
          itemsToAggregate.forEach(item => {
            const attributeValue = getAttributeValue(item, colorByAttribute) || '<not set>';
            uniqueSubgroups.add(attributeValue);
            aggregationAttribute.forEach(attr => {
              let isMatch = false;
              if (attr.includes('Milestone') && item.type === 'milestone') {
                isMatch = true;
              } else if (attr.includes('workstreamAction') && item.type === 'actionItem') {
                isMatch = true;
              }

              if (isMatch) {
                const calcFunc = getCalculationFunction(attr, false);
                const value = calcFunc([item]);
                if (!tempGroupedData[attr]) {
                  tempGroupedData[attr] = { key: attr, metric: getTitleForAttribute(attr) };
                }
                if (!tempGroupedData[attr][attributeValue]) {
                  tempGroupedData[attr][attributeValue] = 0;
                }
                tempGroupedData[attr][attributeValue] += value;
              }
            });
          });

          internalDataSource = Object.values(tempGroupedData).map(row => {
            const formattedRow = { key: row.key, metric: row.metric };
            Array.from(uniqueSubgroups).sort().forEach(subgroup => {
              formattedRow[subgroup] = formatMetricValue(row[subgroup] || 0, row.metric);
            });
            return formattedRow;
          });

          internalColumns = [
            {
              title: 'Metrics',
              dataIndex: 'metric',
              key: 'metric',
              fixed: 'left',
              width: 250,
              render: (text) => <strong>{text}</strong>
            },
            ...Array.from(uniqueSubgroups).sort().map(subgroup => ({
              title: (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor:
                        initiativeColors?.[subgroup] ||
                        milestoneColors?.[subgroup] ||
                        actionItemColors?.[subgroup] ||
                        '#ccc',
                      marginRight: 8,
                    }}
                  />
                  {subgroup}
                </div>
              ),
              dataIndex: subgroup,
              key: subgroup,
              render: (value) => value || '0',
            }))
          ];
          if (sumVisible) {
            // Add total column for swapped view
            internalColumns.push({
              title: 'Total',
              dataIndex: 'total',
              key: 'total',
              fixed: 'right',
              render: (value, record) => {
                const rowTotal = Array.from(uniqueSubgroups).reduce((acc, subgroup) => acc + (parseFloat(record[subgroup]) || 0), 0);
                return <strong>{formatMetricValue(rowTotal, 'total')}</strong>;
              }
            });
          }

        } else {
          const aggregatedData = {};
          myInitiatives.forEach(item => {
            const attributeValue = getAttributeValue(item, colorByAttribute) || '<not set>';
            const color = initiativeColors?.[attributeValue] || '#ccc';
            if (!aggregatedData[attributeValue]) {
              aggregatedData[attributeValue] = {
                key: attributeValue,
                displayValue: attributeValue,
                color: color,
                initiativesInGroup: [],
                milestonesInGroup: [],
                actionItemsInGroup: [],
              };
            }
            aggregatedData[attributeValue].initiativesInGroup.push(item);
          });
          myMilestones.forEach((milestone) => {
            const attributeValue = getAttributeValue(milestone, colorByAttribute) || '<not set>';
            const color = milestoneColors?.[attributeValue] || '#ccc';
            if (!aggregatedData[attributeValue]) {
              aggregatedData[attributeValue] = {
                key: attributeValue,
                displayValue: attributeValue,
                color: color,
                initiativesInGroup: [],
                milestonesInGroup: [],
                actionItemsInGroup: [],
              };
            }
            aggregatedData[attributeValue].milestonesInGroup.push(milestone);
          });
          myActionItems.forEach((action) => {
            const attributeValue = getAttributeValue(action, colorByAttribute) || '<not set>';
            const color = actionItemColors?.[attributeValue] || '#ccc';
            if (!aggregatedData[attributeValue]) {
              aggregatedData[attributeValue] = {
                key: attributeValue,
                displayValue: attributeValue,
                color: color,
                initiativesInGroup: [],
                milestonesInGroup: [],
                actionItemsInGroup: [],
              };
            }
            aggregatedData[attributeValue].actionItemsInGroup.push(action);
          });
          internalDataSource = Object.values(aggregatedData);

          internalDataSource.forEach(group => {
            aggregationAttribute.forEach(attr => {
              const calcFunc = getCalculationFunction(attr, false);
              const items = group.initiativesInGroup || [];
              const milestonesItems = group.milestonesInGroup || [];
              const actionItems = group.actionItemsInGroup || [];

              if (attr?.includes('Milestone')) {
                group[attr] = calcFunc(milestonesItems);
              } else if (attr?.includes('workstreamAction')) {
                group[attr] = calcFunc(actionItems);
              } else {
                group[attr] = calcFunc(items);
              }
            });
          });

          internalColumns = [{
            title: colorByAttribute && colorByAttribute !== 'None' ? getTitleForAttribute(colorByAttribute) : 'Group',
            dataIndex: 'displayValue',
            key: 'group',
            render: (text, record) => colorByAttribute && colorByAttribute !== 'None' ? (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: record.color, marginRight: '8px' }} />
                <span>{text}</span>
              </div>
            ) : <b>{text}</b>, // Render as bold if no color attribute for top-level group
          }];
          internalColumns = internalColumns.concat(aggregationAttribute.map(attr => ({
            title: getTitleForAttribute(attr),
            dataIndex: attr,
            key: attr,
            render: (value) => formatMetricValue(value || 0, attr),
          })));
          if (sumVisible) {
            // When swapVisible is false, add Total as a bottom row
            const totalRow = {
              key: 'total-row',
              displayValue: 'Total',
              isTotalRow: true,
              color: '#ccc',
            };

            // Calculate column totals
            aggregationAttribute.forEach(attr => {
              const columnTotal = internalDataSource.reduce((sum, group) => {
                return sum + (parseFloat(group[attr]) || 0);
              }, 0);
              totalRow[attr] = formatMetricValue(columnTotal, attr);
            });

            internalDataSource.push(totalRow);
          }
        }
      } else {
        if (swapVisible) {
          // Swapped view: metrics as rows, values as columns
          internalDataSource = aggregationAttribute.map(attr => {
            const row = {
              key: attr,
              metric: getTitleForAttribute(attr),
            };

            // Calculate the value for this metric across all initiatives
            const calcFunc = getCalculationFunction(attr, false);
            let value = 0;

            if (attr.includes('Milestone')) {
              value = calcFunc(myMilestones);
            } else if (attr.includes('workstreamAction')) {
              value = calcFunc(myActionItems);
            } else {
              value = calcFunc(myInitiatives);
            }

            row.value = value;
            return row;
          });

          internalColumns = [
            {
              title: 'Metrics',
              dataIndex: 'metric',
              key: 'metric',
              fixed: 'left',
              width: 250,
              render: (text) => <strong>{text}</strong>,
            },
            {
              title: 'All Initiatives',
              dataIndex: 'value',
              key: 'value',
              render: (value, record) => formatMetricValue(value || 0, record.metric),
            }
          ];

          if (sumVisible) {
            // Add total column for swapped view
            internalColumns.push({
              title: 'Total',
              dataIndex: 'total',
              key: 'total',
              fixed: 'right',
              render: (value, record) => {
                const rowTotal = parseFloat(record.value) || 0;
                return <strong>{formatMetricValue(rowTotal, 'total')}</strong>;
              },
            });
          }
        } else {
          // Original non-swapped view
          internalDataSource = [{
            key: 'all-data',
            displayValue: 'All Initiatives',
            initiativesInGroup: myInitiatives,
            milestonesInGroup: myMilestones,
            actionItemsInGroup: myActionItems,
          }];

          internalDataSource.forEach(group => {
            aggregationAttribute.forEach(attr => {
              const calcFunc = getCalculationFunction(attr, false);
              const items = group.initiativesInGroup || [];
              const milestonesItems = group.milestonesInGroup || [];
              const actionItems = group.actionItemsInGroup || [];

              if (attr.includes('Milestone')) {
                group[attr] = calcFunc(milestonesItems);
              } else if (attr.includes('workstreamAction')) {
                group[attr] = calcFunc(actionItems);
              } else {
                group[attr] = calcFunc(items);
              }
            });
          });

          internalColumns = [{
            title: 'Metrics',
            dataIndex: 'displayValue',
            key: 'group',
            render: (text) => <b>{text}</b>,
          }];
          internalColumns = internalColumns.concat(aggregationAttribute.map(attr => ({
            title: getTitleForAttribute(attr),
            dataIndex: attr,
            key: attr,
            render: (value) => formatMetricValue(value || 0, attr),
          })));
          if (sumVisible) {
            // When swapVisible is false, add Total as a bottom row
            const totalRow = {
              key: 'total-row',
              displayValue: 'Total',
              isTotalRow: true,
            };

            // Calculate column totals (same as the single row values in this case)
            aggregationAttribute.forEach(attr => {
              totalRow[attr] = formatMetricValue(internalDataSource[0][attr] || 0, attr);
            });

            internalDataSource.push(totalRow);
          }
        }
      }
    }
    return { dataSource: internalDataSource, columns: internalColumns };
  }, [
    myInitiatives, myActionItems, myMilestones, users, aggregationAttribute, colorByAttribute,
    initiativeColors, milestoneColors, actionItemColors, swapVisible, selectedMetric, sumVisible,
    groupedImpact, selectedImpactGroup, groupedData, selectedGroup, selectedDateRange,
    currentMonthIndex, subworkstream, currentUser
  ]);

  useEffect(() => {
    // Only proceed if dataSource is available and has potential expandable rows
    if (!dataSource || dataSource.length === 0) {
      setExpandedRowKeys([]); // Ensure it's empty if no data or no expandable rows
      return;
    }

    // Get all keys of parent rows that actually have children
    const allParentKeys = dataSource.filter(d => d.children && d.children.length > 0).map(d => d.key);

    if (isAllGroupsExpanded) {
      setExpandedRowKeys(allParentKeys);
    } else {
      setExpandedRowKeys([]);
    }
  }, [isAllGroupsExpanded, dataSource]);
  const handleExpandedRowsChange = (keys) => {
    setExpandedRowKeys(keys); // Update local state

    // Update parent state (isAllGroupsExpanded) based on the number of expanded keys
    const allExpandableParentKeys = dataSource.filter(d => d.children && d.children.length > 0).map(d => d.key);
    if (keys.length > 0 && keys.length === allExpandableParentKeys.length) { // Check if all are expanded
      setIsAllGroupsExpanded(true);
    } else {
      setIsAllGroupsExpanded(false);
    }
  };

  useEffect(() => {
    if (tableDownloadVisible) {
      const wb = utils.book_new();
      const dataForExcel = [];

      // Helper function to convert value to number or keep as is
      const formatValue = (value) => {
        if (value === undefined || value === null) return 0;
        const num = parseFloat(value);
        return isNaN(num) ? value : num; // Return number if parsable, otherwise original value
      };

      // Logic for selectedImpactGroup download
      if (selectedImpactGroup && (selectedImpactGroup === 'year' || selectedImpactGroup === 'quarter' || selectedImpactGroup === 'month')) {
        const exportPeriodsList = window.exportPeriodsList || [];

        // Determine if we're in the SWAPPED (Periods are rows, Total is a ROW) or UNSWAPPED (Metrics are rows, Total is a COLUMN) view for impact data
        const isImpactSwapView = swapVisible;

        // --- 1. Create Header Row ---
        let headerKeys = [];
        let metricAttributesAsColumns = [];

        if (isImpactSwapView) {
          // SWAPPED VIEW: Periods are ROWS. Metric Attributes are COLUMNS. TOTAL IS A ROW.

          // 1. Get Metric Attribute Headers (e.g., 'Planned Impact Total (ETB Mn)')
          metricAttributesAsColumns = aggregationAttribute
            .filter(attr => ['plannedImpactTotal', 'leImpactTotal', 'actualImpactTotal', 'plannedImpactTotalExcl', 'actualImpactTotalExcl', 'leImpactTotalExcl'].includes(attr))
            .map(attr => getTitleForAttribute(attr));

          // 2. Define the base columns
          if (colorByAttribute && colorByAttribute !== 'None') {
            // Case: colorByAttribute selected (Metric Group -> Period)
            headerKeys.push(getTitleForAttribute(colorByAttribute));
            headerKeys.push('Period');
          } else {
            // Case: colorByAttribute NOT selected (Period is top-level row)
            headerKeys.push('Period');
          }

          // 3. Add Metric Attribute columns
          headerKeys = headerKeys.concat(metricAttributesAsColumns);

          // 4. Add 'Total' as a column header (used for row total in the Grand Total row)
          if (sumVisible) {
            headerKeys.push('Total');
          }

        } else {
          // UNSWAPPED VIEW: Metric Attributes are ROWS. Periods are COLUMNS. TOTAL IS A COLUMN.
          headerKeys.push('Metric');
          headerKeys.push('Metric Attribute');

          headerKeys = headerKeys.concat(exportPeriodsList);

          if (sumVisible) {
            headerKeys.push('Total');
          }
        }

        // Create the single, clean header row object (CRITICAL for avoiding duplicate header rows)
        const headerRow = {};
        headerKeys.forEach(key => {
          headerRow[key] = key;
        });
        dataForExcel.push(headerRow);

        // --- 2. Add Data Rows ---

        // Find the total row and separate it from the main data set for later
        const totalRowItem = dataSource.find(item => item.isTotalRow || item.isGrandTotal);
        const dataRows = dataSource.filter(item => !(item.isTotalRow || item.isGrandTotal));

        dataRows.forEach(item => {

          if (isImpactSwapView) {
            // SWAPPED LOGIC (Periods are rows)

            if (item.children && item.children.length > 0) {
              // Case: colorByAttribute selected (Metric Group -> Period)
              item.children.forEach(child => {
                const row = {
                  [getTitleForAttribute(colorByAttribute)]: item.metric,
                  'Period': child.metric,
                };
                let rowTotal = 0;

                metricAttributesAsColumns.forEach(title => {
                  const dataKey = Object.keys(child).find(key => getTitleForAttribute(key) === title) || title;
                  const value = formatValue(child[dataKey]);
                  row[title] = value;
                  rowTotal += value;
                });

                if (sumVisible) {
                  row['Total'] = rowTotal;
                }
                dataForExcel.push(row);
              });
            } else {
              // Case: colorByAttribute NOT selected (Period is top-level row)
              const row = { 'Period': item.metric };
              let rowTotal = 0;

              metricAttributesAsColumns.forEach(title => {
                const dataKey = Object.keys(item).find(key => getTitleForAttribute(key) === title) || title;
                const value = formatValue(item[dataKey]);
                row[title] = value;
                rowTotal += value;
              });

              if (sumVisible) {
                row['Total'] = rowTotal;
              }
              dataForExcel.push(row);
            }

          } else {
            // UNSWAPPED LOGIC (Metrics are rows) - Total is a COLUMN

            if (item.children && item.children.length > 0) {
              item.children.forEach(child => {
                const row = {
                  'Metric': item.metric,
                  'Metric Attribute': child.metric
                };
                let rowTotal = 0;

                exportPeriodsList.forEach(period => {
                  const value = formatValue(child[period]);
                  row[period] = value;
                  rowTotal += value;
                });

                if (sumVisible) {
                  row['Total'] = rowTotal;
                }
                dataForExcel.push(row);
              });
            } else {
              const row = {
                'Metric': item.metric,
                'Metric Attribute': '-'
              };
              let rowTotal = 0;

              exportPeriodsList.forEach(period => {
                const value = formatValue(item[period]);
                row[period] = value;
                rowTotal += value;
              });

              if (sumVisible) {
                row['Total'] = rowTotal;
              }

              dataForExcel.push(row);
            }
          }
        });

        // --- 3. Handle Total Row (if applicable) ---
        if (totalRowItem && sumVisible) {
          if (isImpactSwapView) {
            // SWAPPED: Total is a ROW. 
            if (totalRowItem.children && totalRowItem.children.length > 0) {
              // Total row when colorByAttribute is selected (Total -> Period)
              totalRowItem.children.forEach(child => {
                const row = {
                  [getTitleForAttribute(colorByAttribute)]: 'Total',
                  'Period': child.metric
                };
                let rowTotal = 0;

                metricAttributesAsColumns.forEach(title => {
                  const dataKey = Object.keys(child).find(key => getTitleForAttribute(key) === title) || title;
                  const value = formatValue(child[dataKey]);
                  row[title] = value;
                  rowTotal += value;
                });
                row['Total'] = rowTotal;
                dataForExcel.push(row);
              });
            } else {
              // Total row when colorByAttribute is NOT selected (Total as top-level row)
              const row = { 'Period': 'Total' };
              let rowTotal = 0;

              metricAttributesAsColumns.forEach(title => {
                const dataKey = Object.keys(totalRowItem).find(key => getTitleForAttribute(key) === title) || title;
                const value = formatValue(totalRowItem[dataKey]);
                row[title] = value;
                rowTotal += value;
              });
              row['Total'] = rowTotal;
              dataForExcel.push(row);
            }
          } else {
            // UNSWAPPED: Total is a COLUMN (handled by child logic if a total row exists)
            if (totalRowItem.children && totalRowItem.children.length > 0) {
              totalRowItem.children.forEach(child => {
                const row = {
                  'Metric': 'Total',
                  'Metric Attribute': child.metric
                };
                let rowTotal = 0;
                exportPeriodsList.forEach(period => {
                  const value = formatValue(child[period]);
                  row[period] = value;
                  rowTotal += value;
                });
                row['Total'] = rowTotal;
                dataForExcel.push(row);
              });
            }
          }
        }


      } else if (selectedGroup && selectedGroup !== 'None' && groupedData) {
        if (swapVisible) {
          // SWAPPED VIEW: Metrics as rows, groups as columns - Total as COLUMN
          const headerRow = { 'Metric': 'Metric' };

          // Get all unique groups from the data
          const uniqueGroups = new Set();
          dataSource.forEach(item => {
            if (item.isTotalRow || item.isGrandTotal) return;

            if (item.children && item.children.length > 0) {
              item.children.forEach(child => {
                Object.keys(child).forEach(key => {
                  if (key !== 'key' && key !== 'metric' && key !== 'isChild' &&
                    key !== 'parentColor' && key !== 'children' && !key.startsWith('_') &&
                    child[key] !== undefined) {
                    uniqueGroups.add(key);
                  }
                });
              });
            } else {
              Object.keys(item).forEach(key => {
                if (key !== 'key' && key !== 'metric' && key !== 'displayValue' &&
                  key !== 'color' && key !== 'children' && !key.startsWith('_') &&
                  item[key] !== undefined) {
                  uniqueGroups.add(key);
                }
              });
            }
          });

          // Add group columns to header
          Array.from(uniqueGroups).sort().forEach(group => {
            headerRow[group] = group;
          });

          // Add Total COLUMN if sumVisible
          if (sumVisible) {
            headerRow['Total'] = 'Total';
          }

          dataForExcel.push(headerRow);

          // Add data rows
          dataSource.forEach(item => {
            if (item.isTotalRow || item.isGrandTotal) return;

            const row = { 'Metric': item.metric };

            // Fill in values for each group
            Array.from(uniqueGroups).sort().forEach(group => {
              if (item.children && item.children.length > 0) {
                let groupTotal = 0;
                item.children.forEach(child => {
                  groupTotal += formatValue(child[group]);
                });
                row[group] = groupTotal;
              } else {
                row[group] = formatValue(item[group]);
              }
            });

            // Calculate row total if sumVisible
            if (sumVisible) {
              const rowTotal = Array.from(uniqueGroups).reduce((sum, group) => {
                return sum + formatValue(row[group]);
              }, 0);
              row['Total'] = rowTotal;
            }

            dataForExcel.push(row);
          });

        } else {
          // UNSWAPPED VIEW: Groups as rows - Total as ROW
          const headerRow = {
            'Group': 'Group',
            'Subgroup': 'Subgroup'
          };

          aggregationAttribute.forEach(attr => {
            headerRow[getTitleForAttribute(attr)] = getTitleForAttribute(attr);
          });

          dataForExcel.push(headerRow);

          // Add data rows
          dataSource.forEach(item => {
            if (item.isTotalRow || item.isGrandTotal) return;

            if (item.children && item.children.length > 0) {
              item.children.forEach(child => {
                const row = {
                  'Group': item.displayValue,
                  'Subgroup': child.displayValue || child.metric
                };

                aggregationAttribute.forEach(attr => {
                  row[getTitleForAttribute(attr)] = formatValue(child[attr]);
                });

                dataForExcel.push(row);
              });
            } else {
              const row = {
                'Group': item.displayValue,
                'Subgroup': '-'
              };

              aggregationAttribute.forEach(attr => {
                row[getTitleForAttribute(attr)] = formatValue(item[attr]);
              });

              dataForExcel.push(row);
            }
          });

          // Add total as a separate ROW
          if (sumVisible) {
            const totalRow = dataSource.find(item => item.isTotalRow || item.isGrandTotal);
            if (totalRow) {
              const grandTotalRow = {
                'Group': 'Total',
                'Subgroup': '-'
              };

              aggregationAttribute.forEach(attr => {
                grandTotalRow[getTitleForAttribute(attr)] = formatValue(totalRow[attr]);
              });

              dataForExcel.push(grandTotalRow);
            }
          }
        }
      } else {
        // DEFAULT SECTION (No grouping, no impact)
        if (swapVisible) {
          // SWAPPED VIEW: Metrics as rows - Total as COLUMN
          const headerRow = {
            'Metric': 'Metric',
            'Value': 'Value'
          };

          if (sumVisible) {
            headerRow['Total'] = 'Total';
          }

          dataForExcel.push(headerRow);

          // Add data rows
          dataSource.forEach(item => {
            if (item.isTotalRow || item.isGrandTotal) return;

            const row = { 'Metric': item.metric };

            let value = 0;
            if (item.value !== undefined) {
              value = formatValue(item.value);
            } else if (aggregationAttribute.length > 0 && item[aggregationAttribute[0]] !== undefined) {
              value = formatValue(item[aggregationAttribute[0]]);
            }

            row['Value'] = value;

            if (sumVisible) {
              row['Total'] = value;
            }

            dataForExcel.push(row);
          });

        } else {
          // UNSWAPPED VIEW: Groups as rows - Total as ROW
          const headerRow = {
            'Group': 'Group'
          };

          aggregationAttribute.forEach(attr => {
            headerRow[getTitleForAttribute(attr)] = getTitleForAttribute(attr);
          });

          dataForExcel.push(headerRow);

          // Add data rows
          dataSource.forEach(item => {
            if (item.isTotalRow || item.isGrandTotal) return;

            const row = {
              'Group': item.displayValue || item.metric
            };

            aggregationAttribute.forEach(attr => {
              row[getTitleForAttribute(attr)] = formatValue(item[attr]);
            });

            dataForExcel.push(row);
          });

          // Add total as a separate ROW
          if (sumVisible) {
            const totalRow = dataSource.find(item => item.isTotalRow || item.isTotal || item.isGrandTotal);
            if (totalRow) {
              const totalData = { 'Group': 'Total' };

              aggregationAttribute.forEach(attr => {
                totalData[getTitleForAttribute(attr)] = formatValue(totalRow[attr]);
              });

              dataForExcel.push(totalData);
            }
          }
        }
      }

      // Create worksheet and download
      if (dataForExcel.length > 0) {
        const ws = utils.json_to_sheet(dataForExcel);
        utils.book_append_sheet(wb, ws, 'Data');
        writeFile(wb, 'InitiativesData.xlsx');
      } else {
        console.warn('No data to export');
      }

      setTableDownloadVisible(false);
    }
  }, [tableDownloadVisible, dataSource, aggregationAttribute, colorByAttribute, selectedImpactGroup, swapVisible, setTableDownloadVisible, columns, selectedGroup, sumVisible, groupedData]);
  useEffect(() => {
    if (copyVisible) {
      let text = '';

      if (swapVisible) {
        text += 'Metric\t';
        if (columns.length > 0) {
          const headerRow = [];
          columns.forEach(col => {
            if (col.children) {
              col.children.forEach(childCol => headerRow.push(childCol.title));
            } else {
              headerRow.push(col.title);
            }
          });
          text += headerRow.join('\t') + '\n';
        }

        dataSource.forEach((item) => {
          text += `${item.metric}\t`;
          columns.forEach(col => {
            if (col.children) {
              col.children.forEach(childCol => {
                const dataKey = childCol.dataIndex;
                const value = item[dataKey];
                text += `${value !== undefined ? value : '0'}\t`;
              });
            } else {
              const dataKey = col.dataIndex;
              const value = item[dataKey];
              text += `${value !== undefined ? value : '0'}\t`;
            }
          });
          text += '\n';
        });

      } else if (selectedImpactGroup && (selectedImpactGroup === 'year' || selectedImpactGroup === 'quarter' || selectedImpactGroup === 'month')) {
        // Headers for impact view copy
        const firstColTitle = colorByAttribute && colorByAttribute !== 'None' ? getTitleForAttribute(colorByAttribute) : 'Metrics';
        text += `${firstColTitle}\tType\t`;

        // Add periods as headers
        const periodsList = window.exportPeriodsList || [];
        text += periodsList.join('\t') + '\tTotal\n';

        // Rows for impact view copy
        dataSource.forEach(item => {
          if (item.isGrandTotal) {
            // Handle Grand Total with its children
            if (item.children && item.children.length > 0) {
              item.children.forEach(child => {
                text += `${item.metric}\t${child.metric}\t`;
                periodsList.forEach(period => {
                  const value = child[period];
                  text += `${value !== undefined ? value : '0'}\t`;
                });
                // Child row total
                const childTotal = periodsList.reduce((sum, period) => sum + (parseFloat(child[period]) || 0), 0);
                text += `${formatMetricValue(childTotal, child.metric)}\n`;
              });
            }
            // Add Grand Total summary row
            text += `${item.metric}\tTotal\t`;
            periodsList.forEach(period => {
              const value = item[period];
              text += `${value !== undefined ? value : '0'}\t`;
            });
            text += `${item.total || '0'}\n`;

          } else if (item.children && item.children.length > 0) {
            // Parent rows with children (color groups like "On Track", "Cancelled")
            item.children.forEach(child => {
              text += `${item.metric}\t${child.metric}\t`;
              periodsList.forEach(period => {
                const value = child[period];
                text += `${value !== undefined ? value : '0'}\t`;
              });
              // Child row total
              const childTotal = periodsList.reduce((sum, period) => sum + (parseFloat(child[period]) || 0), 0);
              text += `${formatMetricValue(childTotal, child.metric)}\n`;
            });

            // Add parent total row
            if (sumVisible) {
              text += `${item.metric}\tTotal\t`;
              periodsList.forEach(period => {
                const periodTotal = item.children.reduce((sum, child) => sum + (parseFloat(child[period]) || 0), 0);
                text += `${formatMetricValue(periodTotal, 'total')}\t`;
              });
              // Parent total
              const parentTotal = periodsList.reduce((sum, period) => {
                const periodTotal = item.children.reduce((childSum, child) => childSum + (parseFloat(child[period]) || 0), 0);
                return sum + periodTotal;
              }, 0);
              text += `${formatMetricValue(parentTotal, 'total')}\n`;
            }
          } else {
            // Single rows without children (when no colorByAttribute)
            text += `${item.metric}\t\t`;
            periodsList.forEach(period => {
              const value = item[period];
              text += `${value !== undefined ? value : '0'}\t`;
            });
            const rowTotal = periodsList.reduce((sum, period) => sum + (parseFloat(item[period]) || 0), 0);
            text += `${formatMetricValue(rowTotal, item.metric)}\n`;
          }
        });

      } else {
        // Headers for non-impact, non-swapped view
        const groupTitle = colorByAttribute && colorByAttribute !== 'None' ?
          getTitleForAttribute(colorByAttribute) :
          (selectedGroup && selectedGroup !== 'None' ? getTitleForAttribute(selectedGroup) : 'Group');

        text += `${groupTitle}\t`;

        // Add aggregation attributes as headers
        aggregationAttribute.forEach(attr => {
          text += `${getTitleForAttribute(attr)}\t`;
        });

        if (sumVisible) {
          text += 'Total\t';
        }
        text += '\n';

        // Rows for non-impact, non-swapped view
        dataSource.forEach(item => {
          if (item.isGrandTotal || item.isTotalRow) {
            // Handle total rows
            text += `${item.metric || item.displayValue}\t`;
            aggregationAttribute.forEach(attr => {
              const value = item[attr];
              text += `${value !== undefined ? formatMetricValue(value, attr) : '0'}\t`;
            });
            if (sumVisible) {
              const total = aggregationAttribute.reduce((sum, attr) => sum + (parseFloat(item[attr]) || 0), 0);
              text += `${formatMetricValue(total, 'total')}\t`;
            }
            text += '\n';

          } else if (item.children && item.children.length > 0) {
            // Parent rows with children
            item.children.forEach(child => {
              text += `${item.displayValue || item.metric}\t`;
              text += `${child.displayValue || child.metric}\t`;
              aggregationAttribute.forEach(attr => {
                const value = child[attr];
                text += `${value !== undefined ? formatMetricValue(value, attr) : '0'}\t`;
              });
              if (sumVisible) {
                const total = aggregationAttribute.reduce((sum, attr) => sum + (parseFloat(child[attr]) || 0), 0);
                text += `${formatMetricValue(total, 'total')}\t`;
              }
              text += '\n';
            });

            // Add parent total row
            if (sumVisible) {
              text += `${item.displayValue || item.metric}\tTotal\t`;
              aggregationAttribute.forEach(attr => {
                const attrTotal = item.children.reduce((sum, child) => sum + (parseFloat(child[attr]) || 0), 0);
                text += `${formatMetricValue(attrTotal, attr)}\t`;
              });
              if (sumVisible) {
                const grandTotal = aggregationAttribute.reduce((sum, attr) => {
                  const attrTotal = item.children.reduce((childSum, child) => childSum + (parseFloat(child[attr]) || 0), 0);
                  return sum + attrTotal;
                }, 0);
                text += `${formatMetricValue(grandTotal, 'total')}\t`;
              }
              text += '\n';
            }
          } else {
            // Single rows without children
            text += `${item.displayValue || item.metric}\t`;
            aggregationAttribute.forEach(attr => {
              const value = item[attr];
              text += `${value !== undefined ? formatMetricValue(value, attr) : '0'}\t`;
            });
            if (sumVisible) {
              const total = aggregationAttribute.reduce((sum, attr) => sum + (parseFloat(item[attr]) || 0), 0);
              text += `${formatMetricValue(total, 'total')}\t`;
            }
            text += '\n';
          }
        });
      }

      // Use a more robust method to copy to clipboard
      const tempInput = document.createElement('textarea');
      tempInput.value = text;
      document.body.appendChild(tempInput);
      tempInput.select();
      tempInput.setSelectionRange(0, 99999); // For mobile

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          alert('Data copied to clipboard!');
        } else {
          alert('Failed to copy data to clipboard.');
        }
      } catch (err) {
        alert('Error copying to clipboard: ' + err);
      }

      document.body.removeChild(tempInput);
      setCopyVisible(false);
    }
  }, [copyVisible, dataSource, aggregationAttribute, colorByAttribute, selectedImpactGroup, swapVisible, setCopyVisible, columns, selectedGroup, sumVisible]);

  // Determine if there are any expandable rows in the current dataSource
  const hasExpandableRows = dataSource.some(d => d.children && d.children.length > 0);

  return (
    <div>
      {/* Expand/Collapse All Button (Visible only if there are expandable rows) */}
      {hasExpandableRows && (
        <Tooltip title={isAllGroupsExpanded ? 'Collapse All Groups' : 'Expand All Groups'}>
          <Button
            onClick={() => setIsAllGroupsExpanded(!isAllGroupsExpanded)}
            style={{ marginBottom: 16 }}
          // icon={isAllGroupsExpanded ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          >
            {/* {isAllGroupsExpanded ? 'Collapse All' : 'Expand All'} */}
          </Button>
        </Tooltip>
      )}
      {/* Ant Design Table */}
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowKey='key'
        bordered={true}
        // Conditionally apply expandable property only if there are expandable rows
        expandable={
          hasExpandableRows
            ? {
              expandedRowKeys,
              onExpandedRowsChange: handleExpandedRowsChange, // Use the dedicated handler
            }
            : undefined // If no expandable rows, explicitly set to undefined
        }
      />
    </div>
  );
};

export default ListView;